import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    IconButton,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    InputAdornment,
    CircularProgress,
    Radio,
    FormControlLabel,
    RadioGroup,
    useMediaQuery,
    useTheme,
    Checkbox,
    Chip,
    Stack,
    Divider,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { notifyPermissionDenied } from '../../utils/apiPermissionError';
import { useAuth } from '../../contexts/AuthContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import api from '../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CuitBcraCheckField from '../shared/CuitBcraCheckField';
import type { BcraCentralDeudoresConsulta, ChequeBcraIngreso } from '../../types/bcraCentralDeudores';
import { buildChequeBcraIngreso } from '../../types/bcraCentralDeudores';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { PersonSearch, Edit, DeleteOutline } from '@mui/icons-material';
import ClienteSearch from './ClienteSearch';
import ClienteInlineSearchField from './ClienteInlineSearchField';
import AplicarSaldoReciboDialog from './AplicarSaldoReciboDialog';
import clienteService, {
    esErrorClienteNoOperativo,
    getMensajeClienteNoOperativo,
    OPTIONS_CLIENTE_OPERATIVO,
} from '../../services/clienteService';
import { formatCurrency, roundMoney, truncToDecimals } from '../../utils/formatters';
import {
    esNroChequeTerceroValido,
    esNroSucursalChequeValido,
    mensajeErrorNroChequeTercero,
    mensajeErrorNroSucursalCheque,
    CHEQUE_NRO_CHEQUE_MAX_DIGITOS,
    CHEQUE_NRO_SUCURSAL_MAX_DIGITOS,
} from '../../utils/chequeTerceroFormats';
import { aplicarCambioTipoSoporteSucursal } from '../../utils/chequeTerceroEstado';
import CurrencyTextField from '../shared/CurrencyTextField';
import { calcularImporteSugeridoPago, calcularTotalMedios, calcularTotalesTesoreria, normalizeImporteMedio } from '../../utils/tesoreriaTotales';
import tesoreriaService, { GrupoCajaChica, MedioTransferencia, MarcaTarjeta, Banco } from '../../services/tesoreriaService';
import tesoreriaAplicacionesService from '../../services/tesoreriaAplicacionesService';
import SearchableSelect from '../SearchableSelect';
import GrupoCajaSelect from './GrupoCajaSelect';
import { findGrupoCajaGuardado, persistGrupoCajaChica } from './grupoCajaStorage';
import { esDebitoImputableEnRecibo, esNotaCreditoCliente } from '../../constants/tiposComprobante';
import NroChequeTextField from './NroChequeTextField';
import { formatNroCheque, NRO_CHEQUE_LEN } from '../../utils/nroCheque';

const TIPO_PAGO_OPTIONS = [
    { value: 'Efectivo', label: 'Efectivo' },
    { value: 'Transferencia', label: 'Transferencias' },
    { value: 'Cheque', label: 'Cheques de Tercero' },
    { value: 'Tarjeta', label: 'Tarjetas' },
    { value: 'Otros', label: 'Otros' },
] as const;

const TIPO_IVA_LABELS: Record<string, string> = {
    I: 'Inscripto',
    N: 'No Inscripto',
    E: 'Exento',
    M: 'Monotributo',
    RI: 'Resp. Inscripto',
    CF: 'Consumidor Final',
    EX: 'Exento',
    MO: 'Monotributista',
    NI: 'No Inscripto',
};

const medioTransferenciaTieneCartera = (medio?: MedioTransferencia | null): boolean =>
    medio != null && Number(medio.cartera) > 0;

const buildTransferMedioLabel = (medio: MedioTransferencia): string =>
    (medio.mcp || medio.descripcion || medio.nombre || '').trim();

const formatCarteraTransferLabel = (
    nombreBanco?: string,
    nroDeCuenta?: string,
    cbu?: string
): string => {
    const parts: string[] = [];
    if (nombreBanco?.trim()) parts.push(nombreBanco.trim());
    if (nroDeCuenta?.trim()) parts.push(`Cta. ${nroDeCuenta.trim()}`);
    else if (cbu?.trim()) {
        const c = cbu.trim();
        parts.push(c.length > 8 ? `CBU ...${c.slice(-4)}` : `CBU ${c}`);
    }
    return parts.join(' · ') || 'Cartera asignada';
};

const filtrarMediosTransferencia = (
    medios: MedioTransferencia[],
    termino: string
): { medio: MedioTransferencia; idx: number }[] => {
    const t = termino.trim().toLowerCase();
    return medios
        .map((medio, idx) => ({ medio, idx }))
        .filter(({ medio }) => {
            if (!t) return true;
            const texto = [
                medio.mcp,
                medio.descripcion,
                medio.nombre,
                medio.nombreBanco,
                medio.cbu,
                medio.nroDeCuenta,
                medio.clase,
                medioTransferenciaTieneCartera(medio)
                    ? formatCarteraTransferLabel(medio.nombreBanco, medio.nroDeCuenta, medio.cbu)
                    : '',
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return texto.includes(t);
        });
};

interface FormularioData {
    nroFormulario?: number;
    tipoCliente: string;
    nroTipoFormulario: string;
    nroSuc: number;
    fechaEmision: string;
    nroCliente: number;
    nombre: string;
    tipoIVA: string;
    cuit: string;
    importeTotal: number;
    descripcion: string;
    usuario: string;
    letra?: string;
    nroComprobante?: number;
    subtotal: number;
    iva: number;
    impuestoInterno?: number;
}

interface TipoPagoData {
    nroTipoPago?: number;
    nroFormulario?: number;
    tipoPago: string;
    ingresoEgreso: string;
    detalle: string;
    // Etiqueta de UI para mostrar el medio elegido (no se envía al backend)
    medioLabel?: string;
    importe: number;
    nroCuenta?: number;
    fecha?: string;
    grupoMcp?: number;
    // Identificador del medio de cobro (para el endpoint)
    mcp?: string;
    cartera?: number;
    carteraLabel?: string;
    carteraRequerida?: boolean;
    // Campos para Cheques de Tercero
    banco?: string;
    codigoBarrasCheque?: string;
    nroBanco?: number;
    nroSucursal?: number;
    nroCheque?: string;
    fechaEmision?: string;
    fechaVencimiento?: string;
    ctaBancaria?: string;
    librador?: string;
    nroLibrador?: string;
    concepto?: string;
    /** true = electrónico; false/undefined = físico (default). */
    esElectronico?: boolean;
    // Campos para Tarjetas
    nroTarjeta?: string;
    nroComprobanteTa?: number;
    numeroroAutorizacion?: string;
    codigoMarca?: number;
    cuotas?: number;
    telefono?: string;
    fechaPresentacion?: string;
    titular?: string;
    /** Otros: fecha de registro del comprobante (opcional). */
    fechaRegistro?: string;
    /** Otros: número de comprobante/certificado (opcional). */
    nroComprobante?: number;
    /** Snapshot BCRA al ingresar cheque (opcional). */
    bcraIngreso?: ChequeBcraIngreso;
}

interface ComprobanteDeuda {
    id: number;
    nroComprobante: string;
    tipoComprobante: string;
    nroTipoFormulario?: string;
    fechaEmision: string;
    fechaVencimiento: string;
    importe: number;
    saldo: number;
    vencido: boolean;
    nroFormulario?: number;
}

const textoComprobanteDeuda = (comp: ComprobanteDeuda): string =>
    `${comp.tipoComprobante} ${comp.nroComprobante}`.trim();

const esComprobanteImputableRecibo = (comp: ComprobanteDeuda): boolean =>
    esDebitoImputableEnRecibo(comp.nroTipoFormulario);

const esNcVisibleEnRecibo = (
    comp: ComprobanteDeuda,
    ncConSaldoPendiente?: Set<number> | null
): boolean => {
    if (!esNotaCreditoCliente(comp.nroTipoFormulario ?? '', textoComprobanteDeuda(comp))) {
        return false;
    }
    const nro = Number(comp.nroFormulario);
    if (ncConSaldoPendiente != null) {
        return Number.isFinite(nro) && nro > 0 && ncConSaldoPendiente.has(nro);
    }
    return Math.abs(Number(comp.saldo ?? 0)) > 0.00001;
};

const esComprobanteVisibleRecibo = (
    comp: ComprobanteDeuda,
    ncConSaldoPendiente?: Set<number> | null
): boolean =>
    esComprobanteImputableRecibo(comp) || esNcVisibleEnRecibo(comp, ncConSaldoPendiente);

interface Sucursal {
    nroSuc: number;
    nombreSucursal: string;
    iniciarEn?: string;
    tipoSuc?: string;
}

// Se cargan dinámicamente desde API

const initialPago: TipoPagoData = {
    tipoPago: '',
    ingresoEgreso: 'I',
    detalle: '',
    importe: 0,
    cuotas: 1,
    fecha: new Date().toISOString().split('T')[0],
    fechaEmision: new Date().toISOString().split('T')[0],
    fechaVencimiento: new Date().toISOString().split('T')[0],
    esElectronico: false,
};

/** Efectivo: permite vuelto (importe negativo). Otros medios: solo importe positivo. */
function importePagoDialogValido(importe: number, tipoPagoSeleccionado: string): boolean {
    const imp = Number(importe);
    if (!Number.isFinite(imp) || imp === 0) return false;
    if ((tipoPagoSeleccionado || '').toLowerCase() === 'efectivo') return true;
    return imp > 0;
}

/** Línea de medio lista para enviar al API (vuelto solo en efectivo). */
function importeMedioReciboValidoParaEnvio(pago: { tipoPago?: string; importe?: number }): boolean {
    if (!pago.tipoPago) return false;
    const imp = Number(pago.importe);
    if (!Number.isFinite(imp) || imp === 0) return false;
    if ((pago.tipoPago || '').toLowerCase() === 'efectivo') return true;
    return imp > 0;
}

const getLastDayOfCurrentMonthISO = (): string => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toISOString().split('T')[0];
};

const parseCodigoBarrasCheque = (input: string): {
    nroBanco: number;
    nroSucursal: number;
    nroCheque: string;
    nroLibrador: string;
} | null => {
    const digits = (input || '').replace(/\D/g, '');
    if (digits.length < 30) {
        return null;
    }

    // Mapeo heredado (posiciones 1-based del sistema anterior):
    // banco=2..4, sucursal=5..7, nroCheque=12..19, librador=20..30
    const nroBancoStr = digits.substring(1, 4);
    const nroSucursalStr = digits.substring(4, 7);
    const nroCheque = digits.substring(11, 19);
    const nroLibrador = digits.substring(19, 30);

    if (!nroBancoStr || !nroSucursalStr || !nroCheque || !nroLibrador) {
        return null;
    }

    const nroBanco = Number(nroBancoStr);
    const nroSucursal = Number(nroSucursalStr);
    if (!Number.isFinite(nroBanco) || !Number.isFinite(nroSucursal)) {
        return null;
    }

    return { nroBanco, nroSucursal, nroCheque, nroLibrador };
};

const buildBancoLabel = (banco: Banco): string =>
    `${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`;

const NuevoRecibo: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const loggedUsername = user?.username || localStorage.getItem('userName') || localStorage.getItem('username') || localStorage.getItem('user') || 'Sistema';

    const [formulario, setFormulario] = useState<FormularioData>({
        tipoCliente: 'C',
        nroTipoFormulario: '4',
        nroSuc: 0,
        fechaEmision: new Date().toISOString().split('T')[0],
        nroCliente: 0,
        nombre: '',
        tipoIVA: '',
        cuit: '',
        importeTotal: 0,
        descripcion: '',
        usuario: loggedUsername,
        subtotal: 0,
        iva: 0
    });

    const [detallesPago, setDetallesPago] = useState<TipoPagoData[]>([]);

    const [openPagoDialog, setOpenPagoDialog] = useState(false);
    const [pagoActual, setPagoActual] = useState<TipoPagoData>(initialPago);
    // Detalle de recibo
    const [openDetalleDialog, setOpenDetalleDialog] = useState(false);
    const [detalleLoading, setDetalleLoading] = useState(false);
    const [detalleRecibo, setDetalleRecibo] = useState<any>(null);
    const [detalleNroFormulario, setDetalleNroFormulario] = useState<number | null>(null);

    const handleVerDetalles = async (nroFormulario?: number) => {
        const id = Number(nroFormulario);
        if (!Number.isFinite(id) || id <= 0) {
            enqueueSnackbar('Este comprobante no tiene nroFormulario para consultar detalle.', { variant: 'info' });
            return;
        }
        setOpenDetalleDialog(true);
        setDetalleLoading(true);
        setDetalleRecibo(null);
        setDetalleNroFormulario(id);
        try {
            const resp = await api.get(`/api/Tesoreria/recibos/${id}/detalle`);
            setDetalleRecibo(resp.data);
        } catch (err: any) {
            const data = err?.response?.data;
            const status = err?.response?.status;
            if (status === 401) {
                enqueueSnackbar('No autorizado para ver el detalle. Inicie sesión nuevamente.', { variant: 'error' });
            } else if (data?.title) {
                enqueueSnackbar(data.title, { variant: 'error' });
            } else {
                enqueueSnackbar('No se pudo obtener el detalle del recibo.', { variant: 'error' });
            }
        } finally {
            setDetalleLoading(false);
        }
    };
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [openClienteSearch, setOpenClienteSearch] = useState(false);

    const [grupoCajaSeleccionado, setGrupoCajaSeleccionado] = useState<GrupoCajaChica | null>(null);
    const [openGrupoCajaDialog, setOpenGrupoCajaDialog] = useState(false);
    const [gruposCaja, setGruposCaja] = useState<GrupoCajaChica[]>([]);
    const [grupoCajaLoading, setGrupoCajaLoading] = useState(false);
    const [grupoCajaSeleccionadoId, setGrupoCajaSeleccionadoId] = useState<number | null>(null);
    const [grupoCajaPage, setGrupoCajaPage] = useState(1);
    const [grupoCajaHasMore, setGrupoCajaHasMore] = useState(true);
    const [cuentaTransferSeleccionadaId, setCuentaTransferSeleccionadaId] = useState<string | number | null>(null);
    const [mediosTransferencia, setMediosTransferencia] = useState<MedioTransferencia[]>([]);
    const [carterasDisponibles, setCarterasDisponibles] = useState<Banco[]>([]);
    const [carteraSeleccionadaId, setCarteraSeleccionadaId] = useState<number | null>(null);
    const [carterasLoading, setCarterasLoading] = useState(false);
    const [transferSearch, setTransferSearch] = useState('');
    const [marcasTarjetas, setMarcasTarjetas] = useState<MarcaTarjeta[]>([]);
    const [bancos, setBancos] = useState<Banco[]>([]);
    const [openBancoDialog, setOpenBancoDialog] = useState(false);
    const [bancoSearch, setBancoSearch] = useState('');
    const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
    const [bancoPage, setBancoPage] = useState(1);
    const [bancoHasMore, setBancoHasMore] = useState(true);
    const [bancoLoading, setBancoLoading] = useState(false);
    const bancosAllLoadedRef = useRef(false);
    const [mediosEfectivo, setMediosEfectivo] = useState<MedioTransferencia[]>([]);
    const [efectivoSeleccionadoId, setEfectivoSeleccionadoId] = useState<number | null>(null);
    const [mediosCheques, setMediosCheques] = useState<MedioTransferencia[]>([]);
    const [chequeSeleccionadoIdx, setChequeSeleccionadoIdx] = useState<string | null>(null);
    const [mediosTarjeta, setMediosTarjeta] = useState<MedioTransferencia[]>([]);
    const [tarjetaSeleccionadaIdx, setTarjetaSeleccionadaIdx] = useState<string | null>(null);
    const [tarjetaPage, setTarjetaPage] = useState(1);
    const [tarjetaHasMore, setTarjetaHasMore] = useState(true);
    const [tarjetaLoading, setTarjetaLoading] = useState(false);
    const [mediosOtros, setMediosOtros] = useState<MedioTransferencia[]>([]);
    const [otrosSeleccionadoIdx, setOtrosSeleccionadoIdx] = useState<string | null>(null);
    const codigoBarrasChequeRef = useRef<HTMLInputElement>(null);
    const importeChequeRef = useRef<HTMLInputElement>(null);
    const fechaVencimientoChequeRef = useRef<HTMLInputElement>(null);
    const guardarPagoBtnRef = useRef<HTMLButtonElement>(null);
    const [bancoCodigoInput, setBancoCodigoInput] = useState('');
    const [bancoNoEncontrado, setBancoNoEncontrado] = useState(false);
    const [bancoCodigoResolviendo, setBancoCodigoResolviendo] = useState(false);

    // Estados para Comprobantes de Deuda
    const [comprobantesDeuda, setComprobantesDeuda] = useState<ComprobanteDeuda[]>([]);
    const [searchComprobante, setSearchComprobante] = useState('');
    const [saldoTotal, setSaldoTotal] = useState(0);
    const [saldoVencido, setSaldoVencido] = useState(0);
    const [saldoVigente, setSaldoVigente] = useState(0);
    const [loadingComprobantes, setLoadingComprobantes] = useState(false);
    const [comprobantesSeleccionados, setComprobantesSeleccionados] = useState<Set<number>>(new Set());
    const [comprobantesExpandidos, setComprobantesExpandidos] = useState<Set<number>>(new Set());
    const [montosAplicar, setMontosAplicar] = useState<Map<number, number>>(new Map());
    const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();
    const [refreshComprobantesKey, setRefreshComprobantesKey] = useState(0);
    const [aplicarNcDialogOpen, setAplicarNcDialogOpen] = useState(false);
    const [aplicarNcComp, setAplicarNcComp] = useState<ComprobanteDeuda | null>(null);

    useEffect(() => {
        const raw = searchParams.get('nroCliente');
        if (raw == null || raw === '') return;
        const num = parseInt(raw, 10);
        if (!Number.isFinite(num) || num <= 0) return;
        setFormulario((prev) => (prev.nroCliente === num ? prev : { ...prev, nroCliente: num }));
    }, [searchParams]);

    useEffect(() => {
        const detalle = searchParams.get('detalle');
        if (detalle == null || detalle.trim() === '') return;
        setFormulario((prev) =>
            prev.descripcion === detalle.trim()
                ? prev
                : { ...prev, descripcion: detalle.trim().slice(0, 200) },
        );
    }, [searchParams]);
    
    const {
        totalMedios,
        totalAplicado,
        diferencia,
        diferenciaInsuficiente,
        diferenciaSinSaldo,
    } = useMemo(() => {
        const importesMedios = detallesPago.map((p) => p.importe || 0);
        const montos =
            comprobantesSeleccionados.size === 0
                ? []
                : Array.from(comprobantesSeleccionados).map((id) => {
                      const comp = comprobantesDeuda.find((c) => c.id === id);
                      if (!comp) return 0;
                      const montoAplicar = montosAplicar.get(id);
                      const monto =
                          montoAplicar !== undefined && montoAplicar !== null
                              ? montoAplicar
                              : comp.saldo || 0;
                      return roundMoney(monto);
                  });
        return calcularTotalesTesoreria(importesMedios, montos);
    }, [detallesPago, comprobantesSeleccionados, comprobantesDeuda, montosAplicar]);

    // Determinar si el cliente puede ser modificado
    const clienteBloqueado = useMemo(() => {
        return comprobantesSeleccionados.size > 0 || detallesPago.length > 0;
    }, [comprobantesSeleccionados.size, detallesPago.length]);

    const _tipoCodeFor = (tipoUI: string): string => {
        switch ((tipoUI || '').toLowerCase()) {
            case 'efectivo': return 'E';
            case 'cheque': return 'C';
            case 'tarjeta': return 'T';
            case 'transferencia': return 'TB';
            case 'otros': return 'O';
            default: return '';
        }
    };

    // Autocompletar Librador con el nombre del cliente cuando se selecciona Cheque
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Cheque' && activeStep === 1) {
            if (!pagoActual.librador && formulario.nombre) {
                setPagoActual(prev => ({ ...prev, librador: formulario.nombre.slice(0, 25) }));
            }
        }
    }, [tipoPagoSeleccionado, activeStep, formulario.nombre, pagoActual.librador]);

    // Cargar marcas de tarjeta al seleccionar "Tarjeta"
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Tarjeta' && openPagoDialog) {
            (async () => {
                try {
                    const marcas = await tesoreriaService.getMarcasTarjetas();
                    setMarcasTarjetas(marcas);
                } catch (e) {
                    console.error('No se pudieron cargar las marcas de tarjetas', e);
                    setMarcasTarjetas([]);
                }
            })();
        }
    }, [tipoPagoSeleccionado, openPagoDialog]);

    // Autocompletar titular con nombre del cliente en Tarjeta
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Tarjeta' && activeStep === 1) {
            if (!pagoActual.titular && formulario.nombre) {
                setPagoActual(prev => ({ ...prev, titular: formulario.nombre.slice(0, 25) }));
            }
        }
    }, [tipoPagoSeleccionado, activeStep, formulario.nombre, pagoActual.titular]);

    // Pre-cargar medios de transferencia usando el grupo guardado en localStorage
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Transferencia') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        setGrupoCajaSeleccionado(parsed);
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosTransferenciaPorGrupo(codigo);
                                setMediosTransferencia(medios);
                                setCuentaTransferSeleccionadaId(null);
                            } catch {
                                setMediosTransferencia([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Pre-cargar medios de efectivo usando el grupo guardado
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Efectivo') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        setGrupoCajaSeleccionado(parsed);
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosEfectivoPorGrupo(codigo);
                                setMediosEfectivo(medios);
                                setEfectivoSeleccionadoId(null);
                            } catch {
                                setMediosEfectivo([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Auto-seleccionar cuando solo hay una opción de efectivo
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Efectivo' && mediosEfectivo.length === 1) {
            const unico = mediosEfectivo[0];
            if (unico && efectivoSeleccionadoId !== unico.id) {
                setEfectivoSeleccionadoId(unico.id);
                setPagoActual(prev => ({ ...prev, medioLabel: unico.nombre, mcp: unico.mcp || unico.nombre }));
            }
        }
    }, [tipoPagoSeleccionado, mediosEfectivo, efectivoSeleccionadoId]);

    // Transferencias: auto-seleccionar si hay una sola opción; si hay varias, no seleccionar
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Transferencia') {
            if (mediosTransferencia.length === 1) {
                const unico = mediosTransferencia[0];
                if (unico && String(cuentaTransferSeleccionadaId) !== '0') {
                    setCuentaTransferSeleccionadaId('0');
                    const tieneCartera = medioTransferenciaTieneCartera(unico);
                    setPagoActual(prev => ({
                        ...prev,
                        mcp: unico.mcp || unico.nombre || '',
                        medioLabel: buildTransferMedioLabel(unico),
                        cartera: tieneCartera ? Number(unico.cartera) : undefined,
                        carteraLabel: tieneCartera
                            ? formatCarteraTransferLabel(unico.nombreBanco, unico.nroDeCuenta, unico.cbu)
                            : undefined,
                        carteraRequerida: !tieneCartera,
                    }));
                    if (!tieneCartera) setCarteraSeleccionadaId(null);
                }
            } else if (mediosTransferencia.length > 1) {
                const idx = Number(cuentaTransferSeleccionadaId);
                if (
                    cuentaTransferSeleccionadaId != null &&
                    (isNaN(idx) || idx < 0 || idx >= mediosTransferencia.length)
                ) {
                    setCuentaTransferSeleccionadaId(null);
                }
            }
        }
    }, [tipoPagoSeleccionado, mediosTransferencia, cuentaTransferSeleccionadaId]);

    // Cargar carteras cuando el MCP de transferencia no tiene cartera en maestro
    useEffect(() => {
        if (tipoPagoSeleccionado !== 'Transferencia') return;
        const idx = cuentaTransferSeleccionadaId != null
            ? Number(cuentaTransferSeleccionadaId)
            : (mediosTransferencia.length === 1 ? 0 : -1);
        if (isNaN(idx) || idx < 0 || idx >= mediosTransferencia.length) return;
        const medio = mediosTransferencia[idx];
        if (medioTransferenciaTieneCartera(medio)) return;

        let cancelled = false;
        (async () => {
            setCarterasLoading(true);
            try {
                const mapById = new Map<number, Banco>();
                let pageNumber = 1;
                let hasMore = true;
                while (hasMore && !cancelled) {
                    const items = await tesoreriaService.getCarteraBancosPaged({
                        pageNumber,
                        pageSize: 50,
                    });
                    for (const c of items) {
                        const nro = Number(c.nroBanco ?? c.id);
                        if (Number.isFinite(nro) && nro >= 1000 && !mapById.has(nro)) {
                            mapById.set(nro, c);
                        }
                    }
                    if (items.length < 50) hasMore = false;
                    else pageNumber += 1;
                }
                if (!cancelled) setCarterasDisponibles(Array.from(mapById.values()));
            } catch {
                if (!cancelled) setCarterasDisponibles([]);
            } finally {
                if (!cancelled) setCarterasLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [tipoPagoSeleccionado, cuentaTransferSeleccionadaId, mediosTransferencia]);

    // Cheques: cargar medios por grupo guardado al abrir diálogo y seleccionar tipo
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Cheque') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosChequesPorGrupo(codigo);
                                setMediosCheques(medios);
                                setChequeSeleccionadoIdx(null);
                            } catch {
                                setMediosCheques([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Otros: cargar medios por grupo guardado
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Otros') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosOtrosPorGrupo(codigo);
                                setMediosOtros(medios);
                                setOtrosSeleccionadoIdx(null);
                            } catch {
                                setMediosOtros([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Cheques: auto-seleccionar si hay una sola opción y asegurar mcp
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Cheque' && mediosCheques.length === 1) {
            setChequeSeleccionadoIdx('0');
            const unico = mediosCheques[0];
            if (unico) {
                setPagoActual(prev => ({ ...prev, medioLabel: unico.nombre, mcp: unico.mcp || unico.nombre }));
            }
        }
    }, [tipoPagoSeleccionado, mediosCheques]);

    // Cheques: autofocus en código de barras al entrar al paso 2
    useEffect(() => {
        if (!openPagoDialog || activeStep !== 1 || tipoPagoSeleccionado !== 'Cheque') return;
        const timer = window.setTimeout(() => {
            codigoBarrasChequeRef.current?.focus();
        }, 120);
        return () => window.clearTimeout(timer);
    }, [openPagoDialog, activeStep, tipoPagoSeleccionado]);

    // Cargar bancos en diálogo al abrir o buscar
    useEffect(() => {
        let timer: any;
        const PAGE_SIZE = 20; // Límite del backend Banco (1-20)
        const search = (bancoSearch || '').trim();
        const isNumericSearch = /^\d+$/.test(search);
        const fetchPage = async (reset: boolean) => {
            try {
                setBancoLoading(true);
                if (isNumericSearch) {
                    const allBanks = await tesoreriaService.getBancosAll();
                    const filtered = allBanks.filter((b) => String(b.nroBanco ?? '').includes(search));
                    setBancos(filtered);
                    setBancoHasMore(false);
                    setBancoPage(1);
                    return;
                }

                const pageToLoad = reset ? 1 : bancoPage;
                const items = await tesoreriaService.getBancosPaged({
                    searchTerm: bancoSearch || undefined,
                    pageNumber: pageToLoad,
                    pageSize: PAGE_SIZE,
                });
                setBancoHasMore(items.length > 0);
                setBancoPage(pageToLoad + 1);
                setBancos(prev => {
                    const next = reset ? items : [...prev, ...items];
                    const map = new Map<number, Banco>();
                    for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                    return Array.from(map.values());
                });
            } catch (e) {
                console.error('No se pudieron cargar los bancos', e);
                if (reset) setBancos([]);
            } finally {
                setBancoLoading(false);
            }
        };

        if (openBancoDialog) {
            timer = setTimeout(() => fetchPage(true), 300);
        }
        return () => timer && clearTimeout(timer);
    }, [openBancoDialog, bancoSearch]);

    const handleBancoScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (/^\d+$/.test((bancoSearch || '').trim())) return;
        if (!bancoHasMore || bancoLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            const PAGE_SIZE = 20; // Límite del backend Banco (1-20)
            (async () => {
                try {
                    setBancoLoading(true);
                    const items = await tesoreriaService.getBancosPaged({
                        searchTerm: bancoSearch || undefined,
                        pageNumber: bancoPage,
                        pageSize: PAGE_SIZE,
                    });
                    setBancoHasMore(items.length > 0);
                    setBancoPage(prev => prev + 1);
                    setBancos(prev => {
                        const next = [...prev, ...items];
                        const map = new Map<number, Banco>();
                        for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                        return Array.from(map.values());
                    });
                } finally {
                    setBancoLoading(false);
                }
            })();
        }
    };

    // Tarjetas: cargar medios por grupo (tipo T) con paginado infinito
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Tarjeta') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        (async () => {
                            try {
                                setTarjetaLoading(true);
                                const PAGE_SIZE = 30;
                                const medios = await tesoreriaService.getMediosTarjetaPorGrupoPaged(codigo, { pageNumber: 1, pageSize: PAGE_SIZE });
                                setMediosTarjeta(medios);
                                setTarjetaSeleccionadaIdx(null);
                                setTarjetaHasMore((medios?.length || 0) > 0);
                                setTarjetaPage(2);
                            } catch {
                                setMediosTarjeta([]);
                                setTarjetaHasMore(false);
                            } finally {
                                setTarjetaLoading(false);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Tarjetas: auto-seleccionar si hay única opción y asegurar mcp
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Tarjeta' && mediosTarjeta.length === 1) {
            setTarjetaSeleccionadaIdx('0');
            const unico = mediosTarjeta[0];
            if (unico) {
                const idNum = Number(unico.id);
                setPagoActual(prev => ({
                    ...prev,
                    nroCuenta: Number.isFinite(idNum) ? idNum : undefined,
                    medioLabel: unico.nombre,
                    mcp: unico.mcp || unico.nombre,
                }));
            }
        }
    }, [tipoPagoSeleccionado, mediosTarjeta]);

    const handleFormularioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target as any;
        setFormulario(prev => ({
            ...prev,
            [name]: name === 'nroCliente' || name === 'nroSuc' ? (Number(value) || 0) : value
        }));
    };

    const setNroSucFormulario = useCallback((nroSuc: number) => {
        setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
    }, []);

    const { onSucursalChange } = useSucursalAltaFormulario({
        nroSuc: Number(formulario.nroSuc) || 0,
        setNroSuc: setNroSucFormulario,
        sucursales,
        loadingSucursales,
        enabled: true,
    });

    // Búsqueda automática por número de cliente (p. ej. desde URL) cuando aún no hay nombre cargado
    useEffect(() => {
        if (formulario.nombre) return;
        const value = formulario.nroCliente;
        const handle = setTimeout(() => {
            const nroCliente = parseInt((value ?? 0).toString());
            if (!isNaN(nroCliente) && nroCliente > 0) {
                handleClienteSearch(nroCliente);
            }
        }, 500);
        return () => clearTimeout(handle);
    }, [formulario.nroCliente, formulario.nombre]);

    // Cargar comprobantes de deuda cuando se selecciona un cliente
    useEffect(() => {
        const nroCliente = formulario.nroCliente;
        if (!nroCliente || nroCliente <= 0) {
            setComprobantesDeuda([]);
            setSaldoTotal(0);
            setSaldoVencido(0);
            setSaldoVigente(0);
            return;
        }

        const cargarComprobantes = async () => {
            setLoadingComprobantes(true);
            try {
                console.log('🔍 Cargando comprobantes para cliente:', nroCliente);
                const [response, creditosRes] = await Promise.all([
                    api.get(`/api/CuentaCorriente/cliente/${nroCliente}`),
                    tesoreriaAplicacionesService
                        .getCreditosConSaldoPendiente(nroCliente)
                        .catch(() => null),
                ]);
                const data = response.data;

                /** null = no se pudo consultar aplicaciones; Set = solo NC con saldo pendiente real */
                let ncConSaldoPendiente: Set<number> | null = null;
                const ncSaldoPendienteByFormulario = new Map<number, number>();
                if (creditosRes != null) {
                    ncConSaldoPendiente = new Set<number>();
                    for (const cred of creditosRes) {
                        const nro = Number(cred.nroFormulario);
                        if (!Number.isFinite(nro) || nro <= 0) continue;
                        if (
                            !esNotaCreditoCliente(
                                cred.nroTipoFormulario ?? '',
                                `${cred.letra ?? ''}${cred.nroSuc ?? ''}${cred.nroComprobante ?? ''}`
                            )
                        ) {
                            continue;
                        }
                        const pendiente = roundMoney(cred.saldoPendiente ?? 0);
                        if (pendiente <= 0) continue;
                        ncConSaldoPendiente.add(nro);
                        ncSaldoPendienteByFormulario.set(nro, pendiente);
                    }
                }
                
                console.log('📦 Respuesta del API:', data);
                
                // Manejar diferentes estructuras de respuesta
                let items: any[] = [];
                if (Array.isArray(data)) {
                    items = data;
                } else if (data && Array.isArray(data.items)) {
                    items = data.items;
                } else if (data && Array.isArray(data.data)) {
                    items = data.data;
                } else if (data && typeof data === 'object') {
                    // Si es un objeto único, convertirlo a array
                    items = [data];
                }
                
                console.log('📋 Items procesados:', items);
                
                // Mapear la respuesta del API a la interfaz ComprobanteDeuda
                const comprobantes: ComprobanteDeuda[] = items.map((item: any, index: number) => {
                    // Obtener el saldo - priorizar saldo, luego saldoPendiente, luego saldoActual, y finalmente importe como fallback
                    let saldo = 0;
                    if (item.saldo !== undefined && item.saldo !== null) {
                        saldo = Number(item.saldo);
                    } else if (item.saldoPendiente !== undefined && item.saldoPendiente !== null) {
                        saldo = Number(item.saldoPendiente);
                    } else if (item.saldoActual !== undefined && item.saldoActual !== null) {
                        saldo = Number(item.saldoActual);
                    } else if (item.importe !== undefined && item.importe !== null) {
                        saldo = Number(item.importe);
                    } else if (item.monto !== undefined && item.monto !== null) {
                        saldo = Number(item.monto);
                    } else if (item.total !== undefined && item.total !== null) {
                        saldo = Number(item.total);
                    }
                    
                    // Obtener el importe
                    let importe = 0;
                    if (item.importe !== undefined && item.importe !== null) {
                        importe = Number(item.importe);
                    } else if (item.monto !== undefined && item.monto !== null) {
                        importe = Number(item.monto);
                    } else if (item.total !== undefined && item.total !== null) {
                        importe = Number(item.total);
                    } else {
                        importe = saldo; // Si no hay importe, usar el saldo
                    }
                    
                    // Obtener fecha de vencimiento - probar múltiples variantes del campo
                    let fechaVencimientoStr = item.fechaVencimiento || 
                                             item.fechaVenc || 
                                             item.fechaVto || 
                                             item.fecha_vencimiento ||
                                             item.fecha_venc ||
                                             item.fecha_vto ||
                                             null;
                    
                    console.log('📅 Fecha de vencimiento obtenida del API:', {
                        fechaVencimiento: item.fechaVencimiento,
                        fechaVenc: item.fechaVenc,
                        fechaVto: item.fechaVto,
                        fecha_vencimiento: item.fecha_vencimiento,
                        fecha_venc: item.fecha_venc,
                        fecha_vto: item.fecha_vto,
                        fechaVencimientoStr: fechaVencimientoStr
                    });
                    
                    // Si no hay fecha de vencimiento, usar fecha de emisión + días de plazo (si está disponible)
                    if (!fechaVencimientoStr && item.fechaEmision) {
                        fechaVencimientoStr = item.fechaEmision;
                        console.log('⚠️ No se encontró fecha de vencimiento, usando fecha de emisión:', fechaVencimientoStr);
                    }
                    
                    // Si aún no hay fecha, usar fecha actual como fallback
                    if (!fechaVencimientoStr) {
                        fechaVencimientoStr = new Date().toISOString();
                        console.log('⚠️ No se encontró fecha de vencimiento ni de emisión, usando fecha actual:', fechaVencimientoStr);
                    }
                    
                    // Obtener número de comprobante - priorizar el campo "numero"
                    const nroComprobanteStr = (item.numero?.toString() || item.nroComprobante?.toString() || item.numeroComprobante?.toString() || item.comprobante?.toString() || '').trim();
                    
                    console.log('🔢 Número de comprobante obtenido del API:', {
                        numero: item.numero,
                        nroComprobante: item.nroComprobante,
                        numeroComprobante: item.numeroComprobante,
                        comprobante: item.comprobante,
                        nroComprobanteStr: nroComprobanteStr
                    });
                    
                    // Obtener tipo de comprobante - si no hay tipo, usar "compro" si está disponible
                    let tipoComprobanteStr = (item.tipoComprobante || item.tipo || item.tipoDocumento || item.descripcionTipo || '').trim();
                    if (!tipoComprobanteStr || tipoComprobanteStr === '') {
                        // Si no hay tipo, usar "compro" si está disponible
                        tipoComprobanteStr = (item.compro?.toString() || item.comprobante?.toString() || 'Sin tipo').trim();
                    }
                    
                    // Obtener nroFormulario del API
                    const nroFormulario = item.nroFormulario || item.nroIdformulario || item.formularioId || item.idFormulario || undefined;
                    
                    if (nroFormulario) {
                        console.log(`📝 Comprobante ${nroComprobanteStr} tiene nroFormulario:`, {
                            nroComprobante: nroComprobanteStr,
                            nroFormulario: nroFormulario,
                            itemNroFormulario: item.nroFormulario,
                            itemNroIdformulario: item.nroIdformulario,
                            itemFormularioId: item.formularioId,
                            itemIdFormulario: item.idFormulario
                        });
                    } else {
                        console.warn(`⚠️ Comprobante ${nroComprobanteStr} NO tiene nroFormulario. Campos disponibles:`, {
                            nroComprobante: nroComprobanteStr,
                            itemKeys: Object.keys(item),
                            itemNroFormulario: item.nroFormulario,
                            itemNroIdformulario: item.nroIdformulario,
                            itemFormularioId: item.formularioId,
                            itemIdFormulario: item.idFormulario
                        });
                    }
                    
                    const nroTipoFormulario = (item.nroTipoFormulario ?? item.NroTipoFormulario ?? '').toString().trim() || undefined;

                    const comprobante: ComprobanteDeuda = {
                        id: item.id || item.nroComprobante || item.numeroComprobante || item.comprobanteId || index + 1,
                        nroComprobante: nroComprobanteStr,
                        tipoComprobante: tipoComprobanteStr,
                        nroTipoFormulario,
                        fechaEmision: item.fechaEmision || item.fecha || item.fechaDocumento || item.fechaComprobante || new Date().toISOString(),
                        fechaVencimiento: fechaVencimientoStr,
                        importe: roundMoney(importe),
                        saldo: roundMoney(saldo),
                        vencido: false, // Se calculará después
                        nroFormulario: nroFormulario ? Number(nroFormulario) : undefined
                    };
                    
                    // Calcular si está vencido comparando la fecha de vencimiento con la fecha actual
                    let esVencido = false;
                    
                    // Primero verificar si el API ya indica que está vencido
                    if (item.vencido !== undefined && item.vencido !== null) {
                        esVencido = Boolean(item.vencido);
                        console.log(`📅 API indica vencido: ${esVencido}`);
                    } else {
                        // Si no, calcular comparando fechas
                        try {
                            // Intentar parsear la fecha de vencimiento
                            let fechaVto: Date;
                            
                            // Si es string, intentar parsearlo
                            if (typeof comprobante.fechaVencimiento === 'string') {
                                // Intentar diferentes formatos de fecha
                                const fechaStr = comprobante.fechaVencimiento.trim();
                                
                                // Si tiene formato ISO (YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss)
                                if (fechaStr.includes('T') || fechaStr.match(/^\d{4}-\d{2}-\d{2}/)) {
                                    fechaVto = new Date(fechaStr);
                                } 
                                // Si tiene formato DD/MM/YYYY
                                else if (fechaStr.match(/^\d{2}\/\d{2}\/\d{4}/)) {
                                    const [dia, mes, año] = fechaStr.split('/');
                                    fechaVto = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
                                }
                                // Si tiene formato DD-MM-YYYY
                                else if (fechaStr.match(/^\d{2}-\d{2}-\d{4}/)) {
                                    const [dia, mes, año] = fechaStr.split('-');
                                    fechaVto = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
                                }
                                // Intentar parseo estándar
                                else {
                                    fechaVto = new Date(fechaStr);
                                }
                            } else {
                                fechaVto = new Date(comprobante.fechaVencimiento);
                            }
                            
                            // Validar que la fecha sea válida
                            if (isNaN(fechaVto.getTime())) {
                                console.warn('⚠️ Fecha de vencimiento inválida:', comprobante.fechaVencimiento);
                                esVencido = false;
                            } else {
                                const hoy = new Date();
                                
                                // Normalizar ambas fechas a medianoche para comparar solo fechas, no horas
                                hoy.setHours(0, 0, 0, 0);
                                fechaVto.setHours(0, 0, 0, 0);
                                
                                // Si la fecha de vencimiento es anterior o igual a hoy, está vencido
                                // IMPORTANTE: Si fechaVenc < fechaActual, entonces está vencido
                                esVencido = fechaVto < hoy;
                                
                                // Log detallado para depuración
                                const diffDias = Math.floor((hoy.getTime() - fechaVto.getTime()) / (1000 * 60 * 60 * 24));
                                console.log(`📅 Comparando fechas:`);
                                console.log(`  - Fecha Vencimiento: ${fechaVto.toLocaleDateString('es-AR')} (${fechaVto.toISOString()})`);
                                console.log(`  - Fecha Actual: ${hoy.toLocaleDateString('es-AR')} (${hoy.toISOString()})`);
                                console.log(`  - Diferencia: ${diffDias} días`);
                                console.log(`  - ¿Está vencido? ${esVencido ? '✅ SÍ' : '❌ NO'}`);
                                console.log(`  - Comparación: fechaVto (${fechaVto.getTime()}) < hoy (${hoy.getTime()}) = ${fechaVto.getTime() < hoy.getTime()}`);
                            }
                        } catch (error) {
                            console.error('❌ Error al parsear fecha de vencimiento:', error, 'Fecha:', comprobante.fechaVencimiento);
                            // Si hay error al parsear, considerar como no vencido por defecto
                            esVencido = false;
                        }
                    }
                    
                    comprobante.vencido = esVencido;
                    
                    console.log('✅ Comprobante mapeado:', {
                        ...comprobante,
                        fechaVencimientoFormateada: new Date(comprobante.fechaVencimiento).toLocaleDateString('es-AR'),
                        vencido: comprobante.vencido
                    });
                    
                    return comprobante;
                });
                
                const comprobantesFiltradosRaw = comprobantes
                    .filter((comp) => esComprobanteVisibleRecibo(comp, ncConSaldoPendiente))
                    .map((comp) => {
                        if (!esNcVisibleEnRecibo(comp, ncConSaldoPendiente)) return comp;
                        const nro = Number(comp.nroFormulario);
                        const pendiente = ncSaldoPendienteByFormulario.get(nro);
                        if (pendiente == null) return comp;
                        return { ...comp, saldo: -Math.abs(pendiente) };
                    });

                // Evitar NC duplicadas (la CC a veces trae más de una fila por el mismo nroFormulario)
                const vistosNc = new Set<number>();
                const comprobantesFiltrados = comprobantesFiltradosRaw.filter((comp) => {
                    if (!esNcVisibleEnRecibo(comp, ncConSaldoPendiente)) return true;
                    const nro = Number(comp.nroFormulario);
                    if (!Number.isFinite(nro) || nro <= 0) return true;
                    if (vistosNc.has(nro)) return false;
                    vistosNc.add(nro);
                    return true;
                });
                const comprobantesDebito = comprobantesFiltrados.filter((comp) => esComprobanteImputableRecibo(comp));

                console.log('📊 Total comprobantes:', comprobantes.length, '→ visibles:', comprobantesFiltrados.length, '→ débitos:', comprobantesDebito.length);
                
                // Verificar que todos los comprobantes tengan el campo vencido correctamente calculado
                console.log('🔍 Verificando comprobantes vencidos:');
                comprobantesDebito.forEach((comp, idx) => {
                    console.log(`  Comprobante ${idx + 1}:`, {
                        id: comp.id,
                        nroComprobante: comp.nroComprobante,
                        fechaVencimiento: comp.fechaVencimiento,
                        fechaVencimientoFormateada: new Date(comp.fechaVencimiento).toLocaleDateString('es-AR'),
                        vencido: comp.vencido,
                        saldo: comp.saldo
                    });
                });
                
                setComprobantesDeuda(comprobantesFiltrados);
                
                // Calcular saldos solo con débitos imputables (las NC no entran en Vigente/Vencido/Total)
                const total = comprobantesDebito.reduce((sum, c) => sum + (c.saldo || 0), 0);
                
                // Filtrar comprobantes vencidos y sumar sus saldos
                const comprobantesVencidos = comprobantesDebito.filter(c => c.vencido === true);
                const vencido = comprobantesVencidos.reduce((sum, c) => sum + (c.saldo || 0), 0);
                
                // Filtrar comprobantes vigentes (no vencidos) y sumar sus saldos
                const comprobantesVigentes = comprobantesDebito.filter(c => c.vencido === false);
                const vigente = comprobantesVigentes.reduce((sum, c) => sum + (c.saldo || 0), 0);
                
                console.log('💰 Saldos calculados:');
                console.log('  - Total comprobantes:', comprobantes.length);
                console.log('  - Comprobantes vencidos:', comprobantesVencidos.length);
                console.log('  - Comprobantes vigentes:', comprobantesVigentes.length);
                console.log('  - Saldo Total:', total);
                console.log('  - Saldo Vencido:', vencido);
                console.log('  - Saldo Vigente:', vigente);
                console.log('  - Verificación (Total = Vencido + Vigente):', total === (vencido + vigente) ? '✅ Correcto' : '❌ Error');
                
                setSaldoTotal(total);
                setSaldoVencido(vencido);
                setSaldoVigente(vigente);
            } catch (error: any) {
                console.error('❌ Error al cargar comprobantes de deuda:', error);
                console.error('❌ Detalles del error:', error.response?.data || error.message);
                enqueueSnackbar(
                    error.response?.data?.message || 
                    `Error al cargar comprobantes de deuda: ${error.message}` || 
                    'Error al cargar comprobantes de deuda', 
                    { variant: 'error' }
                );
                setComprobantesDeuda([]);
                setSaldoTotal(0);
                setSaldoVencido(0);
                setSaldoVigente(0);
            } finally {
                setLoadingComprobantes(false);
            }
        };

        cargarComprobantes();
    }, [formulario.nroCliente, enqueueSnackbar, refreshComprobantesKey]);

    // Funciones de pago (comentadas temporalmente)
    // const handlePagoChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    //     const { name, value } = e.target;
    //     const newPagos = [...detallesPago];
    //     newPagos[index] = {
    //         ...newPagos[index],
    //         [name]: name === 'importe' ? Number(value) || 0 : value
    //     };
    //     setDetallesPago(newPagos);

    //     // Actualizar el importe total
    //     const nuevoTotal = newPagos.reduce((sum, pago) => sum + (pago.importe || 0), 0);
    //     setFormulario(prev => ({
    //         ...prev,
    //         importeTotal: nuevoTotal
    //     }));
    // };

    // const addPago = () => {
    //     setDetallesPago(prev => [...prev, { ...initialPago }]);
    // };

    const removePago = (index: number) => {
        setDetallesPago(prev => {
            const newPagos = prev.filter((_, i) => i !== index);
            // Actualizar el importe total
            const nuevoTotal = calcularTotalMedios(newPagos.map((pago) => pago.importe || 0));
            setFormulario(prevForm => ({
                ...prevForm,
                importeTotal: nuevoTotal
            }));
            return newPagos;
        });
    };

    const handleOpenPagoDialog = () => {
        const nroCli = parseInt((formulario.nroCliente ?? 0).toString());
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
            enqueueSnackbar('Debe seleccionar un cliente antes de agregar un pago', { variant: 'warning' });
            return;
        }
        
        const importeInicial = calcularImporteSugeridoPago(diferencia, diferenciaInsuficiente);
        
        setPagoActual({
            ...initialPago,
            fechaPresentacion: getLastDayOfCurrentMonthISO(),
            importe: importeInicial
        });
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setOpenPagoDialog(true);
        // Reset selecciones previas de medios
        setCuentaTransferSeleccionadaId(null);
        setMediosTransferencia([]);
        setCarteraSeleccionadaId(null);
        setCarterasDisponibles([]);
        setTransferSearch('');
        setEfectivoSeleccionadoId(null);
        setMediosEfectivo([]);
        setBancoCodigoInput('');
        setBancoNoEncontrado(false);
        setBancoCodigoResolviendo(false);
    };

    const handleBcraConsulta = useCallback((result: BcraCentralDeudoresConsulta | null) => {
        setPagoActual(prev => ({
            ...prev,
            bcraIngreso: result ? buildChequeBcraIngreso(result) : undefined,
        }));
    }, []);

    const handleClosePagoDialog = () => {
        setOpenPagoDialog(false);
        setPagoActual(initialPago);
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setTransferSearch('');
        setBancoCodigoInput('');
        setBancoNoEncontrado(false);
        setBancoCodigoResolviendo(false);
    };

    const resolverBancoPorNumero = useCallback(async (nroBanco: number): Promise<Banco | null> => {
        const local = bancos.find((b) => Number(b.nroBanco) === nroBanco);
        if (local) return local;

        if (!bancosAllLoadedRef.current) {
            try {
                bancosAllLoadedRef.current = true;
                const allBanks = await tesoreriaService.getBancosAll();
                if (allBanks.length) {
                    setBancos((prev) => {
                        const map = new Map<number, Banco>();
                        for (const b of [...prev, ...allBanks]) if (!map.has(b.id)) map.set(b.id, b);
                        return Array.from(map.values());
                    });
                    return allBanks.find((b) => Number(b.nroBanco) === nroBanco) ?? null;
                }
            } catch (err) {
                console.error('No se pudo resolver banco por número', err);
                bancosAllLoadedRef.current = false;
            }
        }
        return null;
    }, [bancos]);

    const aplicarBancoResuelto = useCallback((nroBanco: number, banco: Banco | null) => {
        if (banco) {
            setPagoActual((prev) => ({
                ...prev,
                nroBanco,
                banco: buildBancoLabel(banco),
            }));
            setBancoNoEncontrado(false);
            return;
        }
        setPagoActual((prev) => ({
            ...prev,
            nroBanco,
            banco: '',
        }));
        if (String(nroBanco).length >= 3) {
            setBancoNoEncontrado(true);
        }
    }, []);

    const handleBancoCodigoChange = useCallback(async (raw: string) => {
        const digits = raw.replace(/\D/g, '').slice(0, 4);
        setBancoCodigoInput(digits);
        setBancoNoEncontrado(false);

        if (!digits) {
            setPagoActual((prev) => ({ ...prev, nroBanco: undefined, banco: '' }));
            return;
        }

        const nroBanco = Number(digits);
        if (!Number.isFinite(nroBanco) || nroBanco <= 0) return;

        if (digits.length < 3) {
            setPagoActual((prev) => ({ ...prev, nroBanco, banco: '' }));
            return;
        }

        setBancoCodigoResolviendo(true);
        try {
            const banco = await resolverBancoPorNumero(nroBanco);
            aplicarBancoResuelto(nroBanco, banco);
        } finally {
            setBancoCodigoResolviendo(false);
        }
    }, [aplicarBancoResuelto, resolverBancoPorNumero]);

    const handleChequeMedioChange = useCallback((idx: string | null) => {
        if (!idx) {
            setChequeSeleccionadoIdx(null);
            return;
        }
        setChequeSeleccionadoIdx(idx);
        const n = Number(idx);
        if (!isNaN(n) && n >= 0 && n < mediosCheques.length) {
            const medioSel = mediosCheques[n];
            setPagoActual(prev => ({
                ...prev,
                medioLabel: medioSel.nombre,
                mcp: medioSel.mcp || medioSel.nombre,
            }));
        }
    }, [mediosCheques]);

    const focusSiguienteCampoCheque = useCallback((pago: TipoPagoData) => {
        if (!importePagoDialogValido(pago.importe ?? 0, 'Cheque')) {
            const el = importeChequeRef.current;
            el?.focus();
            el?.select?.();
            return;
        }
        if (!String(pago.fechaVencimiento ?? '').trim()) {
            fechaVencimientoChequeRef.current?.focus();
            return;
        }
        guardarPagoBtnRef.current?.focus();
    }, []);

    const puedeGuardarPago = useMemo(() => {
        if (activeStep !== 1) return false;
        if (!pagoActual.tipoPago) return false;
        if (!importePagoDialogValido(pagoActual.importe, tipoPagoSeleccionado)) return false;
        if (tipoPagoSeleccionado === 'Efectivo' && !pagoActual.mcp) return false;
        if (tipoPagoSeleccionado === 'Transferencia' && (
            !pagoActual.mcp ||
            (pagoActual.carteraRequerida && (carteraSeleccionadaId == null || carteraSeleccionadaId < 1000))
        )) return false;
        if (tipoPagoSeleccionado === 'Tarjeta' && (pagoActual.codigoMarca == null || Number(pagoActual.codigoMarca) <= 0)) {
            return false;
        }
        if (tipoPagoSeleccionado === 'Cheque' && (
            (mediosCheques.length > 0 && (chequeSeleccionadoIdx == null || chequeSeleccionadoIdx === '')) ||
            !pagoActual.nroCheque ||
            !esNroChequeTerceroValido(pagoActual.nroCheque) ||
            !pagoActual.fechaEmision ||
            !pagoActual.fechaVencimiento ||
            !pagoActual.nroBanco ||
            !pagoActual.banco ||
            bancoNoEncontrado ||
            pagoActual.nroSucursal == null ||
            !esNroSucursalChequeValido(pagoActual.nroSucursal) ||
            !pagoActual.nroLibrador
        )) return false;
        if (tipoPagoSeleccionado === 'Otros' && (mediosOtros.length > 0 && (otrosSeleccionadoIdx == null || otrosSeleccionadoIdx === ''))) {
            return false;
        }
        return true;
    }, [
        activeStep,
        pagoActual,
        tipoPagoSeleccionado,
        carteraSeleccionadaId,
        mediosCheques,
        chequeSeleccionadoIdx,
        mediosOtros,
        otrosSeleccionadoIdx,
        bancoNoEncontrado,
    ]);

    const handleGrupoCajaChange = (grupo: GrupoCajaChica | null) => {
        if (detallesPago.length > 0) return;
        setGrupoCajaSeleccionado(grupo);
        setGrupoCajaSeleccionadoId(grupo?.id ?? null);
        setPagoActual((prev) => ({ ...prev, grupoMcp: grupo?.codigo }));
    };

    const grupoCajaBloqueado = detallesPago.length > 0;

    const cargarMediosPorGrupoYTipo = async (grupo: GrupoCajaChica, tipo: string) => {
        if (!grupo.codigo) return;
        if (tipo === 'Transferencia') {
            const medios = await tesoreriaService.getMediosTransferenciaPorGrupo(grupo.codigo);
            setMediosTransferencia(medios);
            setCuentaTransferSeleccionadaId(null);
        } else if (tipo === 'Efectivo') {
            const medios = await tesoreriaService.getMediosEfectivoPorGrupo(grupo.codigo);
            setMediosEfectivo(medios);
            setEfectivoSeleccionadoId(null);
        } else if (tipo === 'Cheque') {
            const medios = await tesoreriaService.getMediosChequesPorGrupo(grupo.codigo);
            setMediosCheques(medios);
            setChequeSeleccionadoIdx(null);
        } else if (tipo === 'Tarjeta') {
            const PAGE_SIZE = 30;
            const medios = await tesoreriaService.getMediosTarjetaPorGrupoPaged(grupo.codigo, { pageNumber: 1, pageSize: PAGE_SIZE });
            setMediosTarjeta(medios);
            setTarjetaSeleccionadaIdx(null);
            setTarjetaHasMore((medios?.length || 0) > 0);
            setTarjetaPage(2);
        } else if (tipo === 'Otros') {
            const medios = await tesoreriaService.getMediosOtrosPorGrupo(grupo.codigo);
            setMediosOtros(medios);
            setOtrosSeleccionadoIdx(null);
        }
    };

    const ensureGrupoCajaSeleccionado = async (): Promise<boolean> => {
        if (grupoCajaSeleccionado?.codigo) {
            setPagoActual((prev) => ({ ...prev, grupoMcp: grupoCajaSeleccionado.codigo }));
            try {
                await cargarMediosPorGrupoYTipo(grupoCajaSeleccionado, tipoPagoSeleccionado);
            } catch (e) {
                console.error('No se pudieron cargar los medios del grupo seleccionado', e);
                enqueueSnackbar('Error al cargar medios del grupo de caja', { variant: 'error' });
                return false;
            }
            setActiveStep(1);
            return true;
        }

        try {
            setGrupoCajaLoading(true);
            const grupos = await tesoreriaService.getGruposCajaChica({ usuario: loggedUsername });
            const found = findGrupoCajaGuardado(grupos || []);
            if (found) {
                setGrupoCajaSeleccionadoId(found.id);
                setGrupoCajaSeleccionado(found);
                setPagoActual((prev) => ({ ...prev, grupoMcp: found.codigo }));
                await cargarMediosPorGrupoYTipo(found, tipoPagoSeleccionado);
                setActiveStep(1);
                return true;
            }
        } catch (err) {
            console.error('Error al cargar grupos por usuario', err);
            enqueueSnackbar('Error al cargar grupos de caja', { variant: 'error' });
        } finally {
            setGrupoCajaLoading(false);
        }

        setOpenGrupoCajaDialog(true);
        return false;
    };

    useEffect(() => {
        if (!openGrupoCajaDialog) return;
        const loadFirstPage = async () => {
            try {
                setGrupoCajaLoading(true);
                const PAGE_SIZE = 30;
                const data = await tesoreriaService.getGruposCajaChicaPaged({ usuario: loggedUsername, pageNumber: 1, pageSize: PAGE_SIZE });
                setGruposCaja(data || []);
                setGrupoCajaHasMore((data?.length || 0) > 0);
                setGrupoCajaPage(2);
                if (grupoCajaSeleccionado?.id) {
                    setGrupoCajaSeleccionadoId(grupoCajaSeleccionado.id);
                }
            } catch (err: unknown) {
                console.error('Error al cargar grupos de caja chica', err);
                enqueueSnackbar('Error al cargar grupos de caja chica', { variant: 'error' });
                setGruposCaja([]);
                setGrupoCajaHasMore(false);
            } finally {
                setGrupoCajaLoading(false);
            }
        };
        loadFirstPage();
    }, [openGrupoCajaDialog, loggedUsername, enqueueSnackbar, grupoCajaSeleccionado?.id]);

    const handleGrupoCajaScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (!grupoCajaHasMore || grupoCajaLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            const PAGE_SIZE = 30;
            void (async () => {
                try {
                    setGrupoCajaLoading(true);
                    const data = await tesoreriaService.getGruposCajaChicaPaged({ usuario: loggedUsername, pageNumber: grupoCajaPage, pageSize: PAGE_SIZE });
                    const existingIds = new Set(gruposCaja.map((g) => g.id));
                    const nuevos = (data || []).filter((g) => !existingIds.has(g.id));
                    setGrupoCajaHasMore(nuevos.length > 0);
                    if (nuevos.length > 0) {
                        setGrupoCajaPage((prev) => prev + 1);
                        setGruposCaja((prev) => [...prev, ...nuevos]);
                    }
                } finally {
                    setGrupoCajaLoading(false);
                }
            })();
        }
    };

    const handleConfirmarGrupoCaja = async () => {
        if (grupoCajaSeleccionadoId == null) return;
        const selected = gruposCaja.find((g) => g.id === grupoCajaSeleccionadoId);
        if (!selected) return;
        persistGrupoCajaChica(selected);
        setGrupoCajaSeleccionado(selected);
        setPagoActual((prev) => ({ ...prev, grupoMcp: selected.codigo }));
        setOpenGrupoCajaDialog(false);
        setActiveStep(1);
        try {
            await cargarMediosPorGrupoYTipo(selected, tipoPagoSeleccionado);
        } catch (e) {
            console.error('No se pudieron cargar los medios del grupo seleccionado', e);
        }
    };

    const handleTipoPagoChange = async (value: string) => {
        setTipoPagoSeleccionado(value);
        setPagoActual(prev => ({
            ...prev,
            tipoPago: value
        }));

        // Al cambiar tipo, limpiar selección de medios específicos
        if (value === 'Transferencia') {
            setCuentaTransferSeleccionadaId(null);
        }
        if (value === 'Efectivo') {
            setEfectivoSeleccionadoId(null);
        }

        // Para cualquier medio, exigir selección de Grupo de Caja
        await ensureGrupoCajaSeleccionado();
    };

    const handlePagoFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === 'codigoBarrasCheque') {
            const parsed = parseCodigoBarrasCheque(String(value));
            const bancoMatch = parsed
                ? bancos.find((b) => Number(b.nroBanco) === parsed.nroBanco)
                : undefined;

            setPagoActual(prev => {
                const next = {
                    ...prev,
                    codigoBarrasCheque: String(value),
                    ...(parsed
                        ? {
                            nroBanco: parsed.nroBanco,
                            nroSucursal: parsed.nroSucursal,
                            nroCheque: parsed.nroCheque,
                            nroLibrador: parsed.nroLibrador,
                            ...(bancoMatch
                                ? { banco: `${bancoMatch.nombre}${bancoMatch.nroBanco ? ` (${bancoMatch.nroBanco})` : ''}` }
                                : {})
                        }
                        : {})
                };
                if (parsed) {
                    window.setTimeout(() => focusSiguienteCampoCheque(next), 0);
                }
                if (parsed?.nroBanco) {
                    setBancoCodigoInput(String(parsed.nroBanco));
                    setBancoNoEncontrado(false);
                }
                return next;
            });

            if (parsed && !bancoMatch && !bancosAllLoadedRef.current) {
                bancosAllLoadedRef.current = true;
                (async () => {
                    try {
                        const allBanks = await tesoreriaService.getBancosAll();
                        if (!allBanks.length) return;

                        setBancos(prev => {
                            const map = new Map<number, Banco>();
                            for (const b of [...prev, ...allBanks]) if (!map.has(b.id)) map.set(b.id, b);
                            return Array.from(map.values());
                        });

                        const resolvedBank = allBanks.find((b) => Number(b.nroBanco) === parsed.nroBanco);
                        if (!resolvedBank) {
                            setBancoNoEncontrado(true);
                            return;
                        }

                        setBancoNoEncontrado(false);
                        const resolvedLabel = buildBancoLabel(resolvedBank);
                        setPagoActual(prev => {
                            if (Number(prev.nroBanco) !== parsed.nroBanco) return prev;
                            return { ...prev, banco: resolvedLabel };
                        });
                    } catch (err) {
                        console.error('No se pudo resolver banco por código de barras del cheque', err);
                    }
                })();
            }
            return;
        }

        if (name === 'nroCheque') {
            const digits = String(value).replace(/\D/g, '').slice(0, CHEQUE_NRO_CHEQUE_MAX_DIGITOS);
            setPagoActual(prev => ({ ...prev, nroCheque: digits }));
            return;
        }

        if (name === 'nroSucursal') {
            const digits = String(value).replace(/\D/g, '').slice(0, CHEQUE_NRO_SUCURSAL_MAX_DIGITOS);
            setPagoActual(prev => ({
                ...prev,
                nroSucursal: digits === '' ? undefined : Number(digits),
            }));
            return;
        }

        setPagoActual(prev => ({
            ...prev,
            [name]:
                name === 'importe'
                    ? (value === '' ? 0 : roundMoney(Number(value)))
                    : (name === 'nroComprobanteTa' || name === 'codigoMarca' || name === 'nroSucursal' || name === 'cuotas' || name === 'nroComprobante')
                        ? (Number(value) || 0)
                        : value
        }));
    };

    const handleGuardarPago = () => {
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'tarjeta') {
            if (!String(pagoActual.numeroroAutorizacion ?? '').trim()) {
                enqueueSnackbar('Ingresá el número de autorización.', { variant: 'warning' });
                return;
            }
        }

        if ((tipoPagoSeleccionado || '').toLowerCase() === 'cheque') {
            const errCheque = mensajeErrorNroChequeTercero(pagoActual.nroCheque);
            if (errCheque) {
                enqueueSnackbar(errCheque, { variant: 'warning' });
                return;
            }
            const errSuc = mensajeErrorNroSucursalCheque(pagoActual.nroSucursal);
            if (errSuc) {
                enqueueSnackbar(errSuc, { variant: 'warning' });
                return;
            }
            if (bancoNoEncontrado || !pagoActual.banco) {
                enqueueSnackbar('Ingresá un código de banco válido o buscalo por nombre.', { variant: 'warning' });
                return;
            }
        }

        // Si es Cheque/Tarjeta y falta mcp, derivarlo del medio seleccionado
        let pagoAAgregar = { ...pagoActual } as any;
        // Recibo: IngresoEgreso siempre "I"; el vuelto se identifica solo por importe negativo en efectivo.
        pagoAAgregar.ingresoEgreso = 'I';
        pagoAAgregar.importe = truncToDecimals(pagoAAgregar.importe);
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'cheque') {
            if (pagoAAgregar.nroCheque) {
                pagoAAgregar.nroCheque = formatNroCheque(pagoAAgregar.nroCheque);
            }
            if (!pagoAAgregar.mcp) {
                const idx = Number(chequeSeleccionadoIdx);
                if (!isNaN(idx) && idx >= 0 && idx < mediosCheques.length) {
                    const medioSel = mediosCheques[idx];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                } else if (mediosCheques.length === 1) {
                    const medioSel = mediosCheques[0];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                }
            }
        }
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'tarjeta') {
            if (!pagoAAgregar.mcp) {
                const idx = Number(tarjetaSeleccionadaIdx);
                if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                    const medioSel = mediosTarjeta[idx];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                } else if (mediosTarjeta.length === 1) {
                    const medioSel = mediosTarjeta[0];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                }
            }
        }
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'transferencia') {
            if (!pagoAAgregar.mcp) {
                const idx = cuentaTransferSeleccionadaId != null
                    ? Number(cuentaTransferSeleccionadaId)
                    : (mediosTransferencia.length === 1 ? 0 : -1);
                if (!isNaN(idx) && idx >= 0 && idx < mediosTransferencia.length) {
                    const medioSel = mediosTransferencia[idx];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                    pagoAAgregar.medioLabel = buildTransferMedioLabel(medioSel);
                    const tieneCartera = medioTransferenciaTieneCartera(medioSel);
                    pagoAAgregar.carteraRequerida = !tieneCartera;
                    if (tieneCartera) {
                        pagoAAgregar.carteraLabel = formatCarteraTransferLabel(
                            medioSel.nombreBanco,
                            medioSel.nroDeCuenta,
                            medioSel.cbu
                        );
                    } else if (carteraSeleccionadaId != null && carteraSeleccionadaId >= 1000) {
                        pagoAAgregar.cartera = carteraSeleccionadaId;
                        const cart = carterasDisponibles.find(
                            c => Number(c.nroBanco ?? c.id) === carteraSeleccionadaId
                        );
                        pagoAAgregar.carteraLabel = cart?.nombre ?? `Cartera ${carteraSeleccionadaId}`;
                    }
                }
            } else if (pagoAAgregar.carteraRequerida && carteraSeleccionadaId != null && carteraSeleccionadaId >= 1000) {
                pagoAAgregar.cartera = carteraSeleccionadaId;
            }
        }

        pagoAAgregar.importe = normalizeImporteMedio(Number(pagoAAgregar.importe) || 0);

        setDetallesPago(prev => {
            const actualizados = [...prev, pagoAAgregar];
            const nuevoTotal = calcularTotalMedios(actualizados.map((pago) => pago.importe || 0));
            setFormulario(prevForm => ({
                ...prevForm,
                importeTotal: nuevoTotal
            }));
            return actualizados;
        });
        handleClosePagoDialog();
    };

    const handlePagoDialogKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (activeStep !== 1) return;
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            if (puedeGuardarPago) handleGuardarPago();
        }
    }, [activeStep, puedeGuardarPago, handleGuardarPago]);

    const handleClienteSelect = (cliente: any) => {
        // Verificar si el cliente está bloqueado
        if (clienteBloqueado) {
            enqueueSnackbar('No se puede modificar el cliente cuando hay comprobantes aplicados o medios de cobro asignados', { variant: 'warning' });
            setOpenClienteSearch(false);
            return;
        }
        
        console.log('👤 Cliente seleccionado:', cliente);
        setFormulario(prev => ({
            ...prev,
            nroCliente: cliente.nroCliente,
            nombre: cliente.nombre,
            cuit: cliente.cuit || '',
            tipoIVA: cliente.tipoIva || ''
        }));
        setOpenClienteSearch(false);
        enqueueSnackbar(`Cliente seleccionado: ${cliente.nombre}`, { variant: 'success' });
    };

    const handleClienteClear = () => {
        if (clienteBloqueado) {
            enqueueSnackbar('No se puede quitar el cliente cuando hay comprobantes aplicados o medios de cobro asignados', { variant: 'warning' });
            return;
        }
        setFormulario((prev) => ({
            ...prev,
            nroCliente: 0,
            nombre: '',
            cuit: '',
            tipoIVA: '',
        }));
    };

    const handleClienteSearch = async (nroCliente: number) => {
        try {
            console.log('🔍 Buscando cliente por número:', nroCliente);
            const cliente = await clienteService.getCliente(nroCliente, OPTIONS_CLIENTE_OPERATIVO);
            console.log('✅ Cliente encontrado:', cliente);
            
            setFormulario(prev => ({
                ...prev,
                nroCliente: cliente.nroCliente,
                nombre: cliente.nombre,
                cuit: cliente.cuit || '',
                tipoIVA: cliente.tipoIva || ''
            }));
            
            enqueueSnackbar(`Cliente encontrado: ${cliente.nombre}`, { variant: 'success' });
        } catch (err: unknown) {
            console.error('❌ Error al buscar cliente:', err);
            if (esErrorClienteNoOperativo(err)) {
                enqueueSnackbar(getMensajeClienteNoOperativo(err, nroCliente), { variant: 'warning' });
            } else {
                enqueueSnackbar(`No se encontró el cliente con número ${nroCliente}`, { variant: 'error' });
            }
        }
    };

    const handlePagarTodo = () => {
        const debitos = comprobantesDeuda.filter((comp) => esComprobanteImputableRecibo(comp));
        if (debitos.length === 0 || saldoTotal === 0) {
            enqueueSnackbar('No hay comprobantes para pagar', { variant: 'warning' });
            return;
        }

        const todosLosIds = new Set(debitos.map(comp => comp.id));
        setComprobantesSeleccionados(todosLosIds);
        
        // Inicializar los montos a aplicar con el saldo de cada comprobante
        const nuevosMontos = new Map<number, number>();
        debitos.forEach(comp => {
            nuevosMontos.set(comp.id, roundMoney(comp.saldo || 0));
        });
        setMontosAplicar(nuevosMontos);
        
        const cantidadSeleccionada = todosLosIds.size;
        enqueueSnackbar(`${cantidadSeleccionada} comprobante${cantidadSeleccionada > 1 ? 's' : ''} seleccionado${cantidadSeleccionada > 1 ? 's' : ''}`, { variant: 'success' });
    };

    const handleToggleComprobante = (id: number) => {
        const compTarget = comprobantesDeuda.find(c => c.id === id);
        if (compTarget && !esComprobanteImputableRecibo(compTarget)) {
            return;
        }
        setComprobantesSeleccionados(prev => {
            const nuevo = new Set(prev);
            if (nuevo.has(id)) {
                nuevo.delete(id);
                // Eliminar el monto a aplicar cuando se deselecciona
                setMontosAplicar(prevMap => {
                    const nuevoMap = new Map(prevMap);
                    nuevoMap.delete(id);
                    return nuevoMap;
                });
            } else {
                nuevo.add(id);
                // Inicializar el monto a aplicar con el saldo del comprobante
                const comp = comprobantesDeuda.find(c => c.id === id);
                if (comp) {
                    setMontosAplicar(prevMap => {
                        const nuevoMap = new Map(prevMap);
                        nuevoMap.set(id, roundMoney(comp.saldo || 0));
                        return nuevoMap;
                    });
                }
            }
            return nuevo;
        });
    };

    const handleAbrirAplicarNc = (comp: ComprobanteDeuda) => {
        if (!comp.nroFormulario || comp.nroFormulario <= 0) {
            enqueueSnackbar('La nota de crédito no tiene número de formulario', { variant: 'warning' });
            return;
        }
        if (!(formulario.nroCliente > 0)) {
            enqueueSnackbar('Seleccione un cliente para aplicar la NC.', { variant: 'warning' });
            return;
        }
        setAplicarNcComp(comp);
        setAplicarNcDialogOpen(true);
    };

    const handleCloseAplicarNcDialog = () => {
        setAplicarNcDialogOpen(false);
        setAplicarNcComp(null);
    };

    const handleToggleDetalles = (id: number) => {
        setComprobantesExpandidos(prev => {
            const nuevo = new Set(prev);
            if (nuevo.has(id)) {
                nuevo.delete(id);
            } else {
                nuevo.add(id);
            }
            return nuevo;
        });
    };

    const formatNumeroComprobante = (nroComprobante: string, tipoComprobante: string): string => {
        // Si el número ya tiene el formato correcto (ej: "FCB-0013-00004558"), devolverlo tal cual
        if (nroComprobante.includes('-') && nroComprobante.split('-').length >= 3) {
            return nroComprobante;
        }
        
        // Intentar formatear como "FCB-0013-00004558" basado en el tipo y número
        // Si el tipo tiene un código numérico al inicio (ej: "1 - Factura"), extraerlo
        const tipoMatch = tipoComprobante.match(/^(\d+)\s*-\s*(.+)/);
        let tipoCode = '';
        
        if (tipoMatch) {
            // Si tiene formato "1 - Factura", usar las primeras letras del nombre
            const nombreTipo = tipoMatch[2];
            // Tomar las primeras 3 letras del nombre del tipo
            tipoCode = nombreTipo.substring(0, 3).toUpperCase().replace(/\s/g, '');
            // Si no tiene suficientes letras, completar con las primeras letras del tipo completo
            if (tipoCode.length < 3) {
                tipoCode = tipoComprobante.substring(0, 3).toUpperCase().replace(/\s/g, '');
            }
        } else {
            // Si no, usar las primeras letras del tipo completo
            tipoCode = tipoComprobante.substring(0, 3).toUpperCase().replace(/\s/g, '');
        }
        
        // Si el tipoCode está vacío o es muy corto, usar un código por defecto
        if (!tipoCode || tipoCode.length < 2) {
            tipoCode = 'FCB'; // Código por defecto
        }
        
        // Extraer números del comprobante
        const numeros = nroComprobante.replace(/\D/g, '');
        
        if (numeros.length >= 12) {
            // Si tiene 12 o más dígitos, formatear como "FCB-0013-00004558"
            const parte1 = numeros.substring(0, 4).padStart(4, '0');
            const parte2 = numeros.substring(4, 12).padStart(8, '0');
            return `${tipoCode}-${parte1}-${parte2}`;
        } else if (numeros.length >= 8) {
            // Si tiene 8 o más dígitos, dividir en dos partes
            const parte1 = numeros.substring(0, 4).padStart(4, '0');
            const parte2 = numeros.substring(4).padStart(8, '0');
            return `${tipoCode}-${parte1}-${parte2}`;
        } else if (numeros.length >= 4) {
            // Si tiene 4 o más dígitos, usar formato con dos partes
            const parte1 = numeros.substring(0, 4).padStart(4, '0');
            const parte2 = numeros.substring(4).padStart(8, '0');
            return `${tipoCode}-${parte1}-${parte2}`;
        } else if (numeros.length > 0) {
            // Si tiene menos de 4 dígitos, usar formato simple
            return `${tipoCode}-${numeros.padStart(4, '0')}`;
        }
        
        // Si no hay números, devolver el formato original con el código de tipo
        return `${tipoCode}-${nroComprobante}`;
    };

    const renderCamposPago = () => {
        switch (tipoPagoSeleccionado) {
            case 'Efectivo':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                EFECTIVO - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <Grid container spacing={2}>
                                    {mediosEfectivo.map((medio) => (
                                        <Grid item xs={12} key={medio.id}>
                                            <Card
                                                variant={efectivoSeleccionadoId === medio.id ? 'elevation' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEfectivoSeleccionadoId(medio.id);
                                                    setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                }}
                                            >
                                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <input
                                                        type="radio"
                                                        checked={efectivoSeleccionadoId === medio.id}
                                                        onChange={() => {
                                                            setEfectivoSeleccionadoId(medio.id);
                                                            setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                        }}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                        {medio.descripcion && (
                                                            <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                        )}
                                                        {medio.clase && (
                                                            <Typography variant="caption">{medio.clase}</Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {mediosEfectivo.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">No hay medios de efectivo disponibles para este grupo.</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                allowNegative
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Use un importe negativo para registrar vuelto en efectivo (debe haber otros medios que compensen el total a aplicar).
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Transferencia': {
                const transferIdx = cuentaTransferSeleccionadaId != null
                    ? Number(cuentaTransferSeleccionadaId)
                    : (mediosTransferencia.length === 1 ? 0 : -1);
                const medioTransferActivo = !isNaN(transferIdx) && transferIdx >= 0 && transferIdx < mediosTransferencia.length
                    ? mediosTransferencia[transferIdx]
                    : null;
                const mcpUnico = mediosTransferencia.length === 1;
                const requiereCarteraManual = medioTransferActivo != null && !medioTransferenciaTieneCartera(medioTransferActivo);
                const mediosTransferVisibles = filtrarMediosTransferencia(mediosTransferencia, transferSearch);

                return (
                    <Grid container spacing={2}>
                        {mcpUnico && medioTransferActivo ? (
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Medio de cobro asignado
                                        </Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {medioTransferActivo.mcp || buildTransferMedioLabel(medioTransferActivo)}
                                        </Typography>
                                        {medioTransferActivo.descripcion && (
                                            <Typography variant="body2" color="text.secondary">
                                                {medioTransferActivo.descripcion}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    TRANSFERENCIAS - Seleccione el medio específico:
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Buscar por código MCP, descripción, banco, cuenta o CBU..."
                                    value={transferSearch}
                                    onChange={(e) => setTransferSearch(e.target.value)}
                                    sx={{ mb: 1.5 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                    <RadioGroup
                                        name="transferOption"
                                        value={cuentaTransferSeleccionadaId != null ? String(cuentaTransferSeleccionadaId) : ''}
                                        onChange={(e) => {
                                            const newId = String(e.target.value);
                                            setCuentaTransferSeleccionadaId(newId);
                                            const idx = Number(newId);
                                            if (!isNaN(idx) && idx >= 0 && idx < mediosTransferencia.length) {
                                                const medioSel = mediosTransferencia[idx];
                                                const tieneCartera = medioTransferenciaTieneCartera(medioSel);
                                                setCarteraSeleccionadaId(null);
                                                setPagoActual(prev => ({
                                                    ...prev,
                                                    mcp: medioSel.mcp || medioSel.nombre || '',
                                                    medioLabel: buildTransferMedioLabel(medioSel),
                                                    cartera: tieneCartera ? Number(medioSel.cartera) : undefined,
                                                    carteraLabel: tieneCartera
                                                        ? formatCarteraTransferLabel(medioSel.nombreBanco, medioSel.nroDeCuenta, medioSel.cbu)
                                                        : undefined,
                                                    carteraRequerida: !tieneCartera,
                                                }));
                                            }
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            {mediosTransferVisibles.map(({ medio, idx }) => (
                                                <Grid item xs={12} key={`${medio.mcp ?? medio.id ?? idx}-${idx}`}>
                                                    <Card variant={(String(cuentaTransferSeleccionadaId) === String(idx)) ? 'elevation' : 'outlined'}>
                                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <FormControlLabel
                                                                value={String(idx)}
                                                                control={<Radio />}
                                                                label={
                                                                    <Box>
                                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                        {medio.descripcion && (
                                                                            <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                        )}
                                                                        {medioTransferenciaTieneCartera(medio) && (
                                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                                {formatCarteraTransferLabel(medio.nombreBanco, medio.nroDeCuenta, medio.cbu)}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                }
                                                                sx={{ m: 0 }}
                                                            />
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                            {mediosTransferencia.length === 0 && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">No hay medios de transferencia disponibles para este grupo.</Typography>
                                                </Grid>
                                            )}
                                            {mediosTransferencia.length > 0 && mediosTransferVisibles.length === 0 && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No se encontraron medios que coincidan con la búsqueda.
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </RadioGroup>
                                </Box>
                            </Grid>
                        )}
                        {medioTransferActivo && medioTransferenciaTieneCartera(medioTransferActivo) && (
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Cartera de recepción
                                        </Typography>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {formatCarteraTransferLabel(
                                                medioTransferActivo.nombreBanco,
                                                medioTransferActivo.nroDeCuenta,
                                                medioTransferActivo.cbu
                                            )}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                        {requiereCarteraManual && (
                            <Grid item xs={12}>
                                <SearchableSelect
                                    label="Cartera de recepción"
                                    required
                                    loading={carterasLoading}
                                    noOptionsText={carterasLoading ? 'Cargando carteras...' : 'No hay carteras disponibles'}
                                    options={carterasDisponibles.map((c) => {
                                        const nro = Number(c.nroBanco ?? c.id);
                                        return {
                                            value: nro,
                                            label: `${c.nombre}${nro >= 1000 ? ` (${nro})` : ''}`.trim(),
                                        };
                                    })}
                                    value={carteraSeleccionadaId}
                                    onChange={(nro) => {
                                        setCarteraSeleccionadaId(nro);
                                        const cart = nro != null
                                            ? carterasDisponibles.find((c) => Number(c.nroBanco ?? c.id) === nro)
                                            : undefined;
                                        setPagoActual((prev) => ({
                                            ...prev,
                                            cartera: nro ?? undefined,
                                            carteraLabel: cart?.nombre ?? (nro != null ? `Cartera ${nro}` : undefined),
                                            carteraRequerida: true,
                                        }));
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );
            }

            case 'Tarjeta':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                TARJETAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}
                                onScroll={(e) => {
                                    if (!tarjetaHasMore || tarjetaLoading) return;
                                    const target = e.currentTarget;
                                    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
                                        const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                                        if (!saved) return;
                                        const parsed = JSON.parse(saved);
                                        const codigo = parsed?.codigo;
                                        if (codigo == null) return;
                                        const PAGE_SIZE = 30;
                                        (async () => {
                                            try {
                                                setTarjetaLoading(true);
                                                const items = await tesoreriaService.getMediosTarjetaPorGrupoPaged(codigo, { pageNumber: tarjetaPage, pageSize: PAGE_SIZE });
                                                // evitar duplicados por id
                                                const existing = new Set(mediosTarjeta.map(m => String(m.id)));
                                                const nuevos = (items || []).filter(m => !existing.has(String(m.id)));
                                                setTarjetaHasMore(nuevos.length > 0);
                                                if (nuevos.length > 0) {
                                                    setTarjetaPage(prev => prev + 1);
                                                    setMediosTarjeta(prev => [...prev, ...nuevos]);
                                                }
                                            } finally {
                                                setTarjetaLoading(false);
                                            }
                                        })();
                                    }
                                }}
                            >
                                <RadioGroup
                                    name="tarjetaOption"
                                    value={tarjetaSeleccionadaIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setTarjetaSeleccionadaIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                                            const medioSel = mediosTarjeta[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({
                                                ...prev,
                                                nroCuenta: Number.isFinite(idNum) ? idNum : undefined,
                                                medioLabel: medioSel.nombre,
                                                mcp: medioSel.mcp || medioSel.nombre,
                                            }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        {mediosTarjeta.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(tarjetaSeleccionadaIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTarjeta.length === 0 && !tarjetaLoading && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de tarjeta disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                        {tarjetaLoading && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">Cargando más medios...</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <SearchableSelect
                                label="* Marca"
                                required
                                options={marcasTarjetas.map((m) => ({
                                    value: m.codigoMarca,
                                    label: m.nombreMarca || m.nombre,
                                }))}
                                value={pagoActual.codigoMarca && pagoActual.codigoMarca > 0 ? pagoActual.codigoMarca : null}
                                onChange={(value) =>
                                    setPagoActual((prev) => ({ ...prev, codigoMarca: Number(value) || 0 }))
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="N° de Tarjeta"
                                name="nroTarjeta"
                                value={pagoActual.nroTarjeta || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="N° Comprobante"
                                name="nroComprobanteTa"
                                type="number"
                                value={pagoActual.nroComprobanteTa ?? ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Fecha"
                                name="fecha"
                                type="date"
                                value={pagoActual.fecha}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Cuotas"
                                name="cuotas"
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                value={pagoActual.cuotas ?? ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nº autorización"
                                name="numeroroAutorizacion"
                                value={pagoActual.numeroroAutorizacion || ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Teléfono"
                                name="telefono"
                                value={pagoActual.telefono || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Titular"
                                name="titular"
                                value={((pagoActual.titular || formulario.nombre) || '').slice(0, 25)}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                        </Grid>
                    </Grid>
                );

            case 'Cheque': {
                const barcodeCompleto = Boolean(
                    pagoActual.nroBanco &&
                    pagoActual.nroSucursal &&
                    pagoActual.nroCheque &&
                    pagoActual.nroLibrador
                );
                const chequeMedioLabel = (medio: MedioTransferencia) =>
                    (medio.descripcion || medio.mcp || medio.nombre || '').trim();

                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                inputRef={codigoBarrasChequeRef}
                                label="Código de barras del cheque"
                                name="codigoBarrasCheque"
                                value={pagoActual.codigoBarrasCheque || ''}
                                onChange={handlePagoFieldChange}
                                helperText="Pegá el código para autocompletar. Tab → Monto · Ctrl+Enter → Guardar"
                                autoComplete="off"
                            />
                        </Grid>

                        {mediosCheques.length > 1 && (
                            <Grid item xs={12}>
                                {mediosCheques.length <= 4 ? (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Tipo de cheque
                                        </Typography>
                                        <ToggleButtonGroup
                                            exclusive
                                            fullWidth
                                            value={chequeSeleccionadoIdx ?? ''}
                                            onChange={(_e, val) => {
                                                if (val != null) handleChequeMedioChange(val);
                                            }}
                                            size="small"
                                        >
                                            {mediosCheques.map((medio, idx) => (
                                                <ToggleButton
                                                    key={medio.id ?? idx}
                                                    value={String(idx)}
                                                    sx={{ py: 1, flex: 1, textTransform: 'none' }}
                                                >
                                                    {chequeMedioLabel(medio)}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </Box>
                                ) : (
                                    <SearchableSelect
                                        label="Medio de cobro"
                                        required
                                        options={mediosCheques.map((medio, idx) => ({
                                            value: String(idx),
                                            label: chequeMedioLabel(medio) || `Medio ${idx + 1}`,
                                        }))}
                                        value={chequeSeleccionadoIdx || null}
                                        onChange={handleChequeMedioChange}
                                    />
                                )}
                            </Grid>
                        )}
                        {mediosCheques.length === 1 && (
                            <Grid item xs={12}>
                                <Chip
                                    size="small"
                                    label={chequeMedioLabel(mediosCheques[0])}
                                    color="primary"
                                    variant="outlined"
                                />
                            </Grid>
                        )}
                        {mediosCheques.length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2">No hay medios de cheques disponibles para este grupo.</Typography>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Soporte del cheque
                            </Typography>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                size="small"
                                value={pagoActual.esElectronico ? 'electronico' : 'fisico'}
                                onChange={(_e, val) => {
                                    if (val == null) return;
                                    const esElectronico = val === 'electronico';
                                    setPagoActual((prev) => ({
                                        ...prev,
                                        esElectronico,
                                        nroSucursal: aplicarCambioTipoSoporteSucursal(esElectronico, prev.nroSucursal),
                                    }));
                                }}
                            >
                                <ToggleButton value="fisico" sx={{ textTransform: 'none', flex: 1 }}>
                                    Físico
                                </ToggleButton>
                                <ToggleButton value="electronico" sx={{ textTransform: 'none', flex: 1 }}>
                                    Electrónico
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        {barcodeCompleto && (
                            <Grid item xs={12}>
                                <Chip
                                    icon={<CheckCircleOutlineIcon />}
                                    label={`${pagoActual.banco || `Banco ${pagoActual.nroBanco}`} · Suc ${pagoActual.nroSucursal} · N° ${pagoActual.nroCheque} · CUIT ${pagoActual.nroLibrador}`}
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Cód. banco"
                                value={bancoCodigoInput}
                                onChange={(e) => { void handleBancoCodigoChange(e.target.value); }}
                                placeholder="Ej. 220"
                                required
                                error={bancoNoEncontrado}
                                helperText={
                                    bancoCodigoResolviendo
                                        ? 'Buscando banco...'
                                        : bancoNoEncontrado
                                            ? 'No existe un banco con ese código'
                                            : '3 dígitos · Enter para buscar por nombre'
                                }
                                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                                InputProps={{
                                    endAdornment: bancoCodigoResolviendo ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={18} />
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        importeChequeRef.current?.focus();
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="* Banco"
                                value={pagoActual.banco || ''}
                                placeholder="Se completa al ingresar el código"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setOpenBancoDialog(true)}
                                                size="small"
                                                aria-label="Buscar banco por nombre"
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <NroChequeTextField
                                fullWidth
                                required
                                value={pagoActual.nroCheque || ''}
                                onChange={(nroCheque) => setPagoActual((prev) => ({ ...prev, nroCheque }))}
                                error={Boolean(pagoActual.nroCheque) && !esNroChequeTerceroValido(pagoActual.nroCheque)}
                                helperText={
                                    pagoActual.nroCheque
                                        ? (mensajeErrorNroChequeTercero(pagoActual.nroCheque) ?? `${NRO_CHEQUE_LEN} dígitos`)
                                        : `${NRO_CHEQUE_LEN} dígitos`
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="N° Sucursal"
                                name="nroSucursal"
                                value={pagoActual.nroSucursal ?? ''}
                                onChange={handlePagoFieldChange}
                                required
                                error={pagoActual.nroSucursal != null && !esNroSucursalChequeValido(pagoActual.nroSucursal)}
                                helperText={
                                    pagoActual.nroSucursal != null && String(pagoActual.nroSucursal) !== ''
                                        ? (mensajeErrorNroSucursalCheque(pagoActual.nroSucursal) ?? `${CHEQUE_NRO_SUCURSAL_MAX_DIGITOS} dígitos`)
                                        : `${CHEQUE_NRO_SUCURSAL_MAX_DIGITOS} dígitos`
                                }
                                inputProps={{ inputMode: 'numeric', maxLength: CHEQUE_NRO_SUCURSAL_MAX_DIGITOS }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CurrencyTextField
                                fullWidth
                                inputRef={importeChequeRef}
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        fechaVencimientoChequeRef.current?.focus();
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Fecha de Emisión"
                                name="fechaEmision"
                                type="date"
                                value={pagoActual.fechaEmision}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                inputRef={fechaVencimientoChequeRef}
                                label="Fecha de Vencimiento"
                                name="fechaVencimiento"
                                type="date"
                                value={pagoActual.fechaVencimiento}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && puedeGuardarPago) {
                                        e.preventDefault();
                                        handleGuardarPago();
                                    }
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Cta. Bancaria"
                                name="ctaBancaria"
                                value={pagoActual.ctaBancaria || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Librador"
                                name="librador"
                                value={((pagoActual.librador || formulario.nombre) || '').slice(0, 25)}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CuitBcraCheckField
                                fullWidth
                                label="Cuit Librador"
                                name="nroLibrador"
                                value={pagoActual.nroLibrador || ''}
                                onChange={handlePagoFieldChange}
                                consultaAutomatica
                                onBcraConsulta={handleBcraConsulta}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Concepto"
                                name="concepto"
                                value={pagoActual.concepto || ''}
                                onChange={handlePagoFieldChange}
                                multiline
                                rows={2}
                                inputProps={{ maxLength: 30 }}
                                helperText={`${(pagoActual.concepto || '').length}/30`}
                            />
                        </Grid>
                    </Grid>
                );
            }

            case 'Otros':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                OTROS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="otrosOption"
                                    value={otrosSeleccionadoIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setOtrosSeleccionadoIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosOtros.length) {
                                            const medioSel = mediosOtros[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        {mediosOtros.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(otrosSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosOtros.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Fecha de registro (opcional)"
                                name="fechaRegistro"
                                type="date"
                                value={pagoActual.fechaRegistro || ''}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Número de comprobante (opcional)"
                                name="nroComprobante"
                                type="number"
                                value={
                                    pagoActual.nroComprobante != null && pagoActual.nroComprobante > 0
                                        ? pagoActual.nroComprobante
                                        : ''
                                }
                                onChange={handlePagoFieldChange}
                                inputProps={{ min: 1, step: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const detallesValidos = detallesPago
                .filter((pago) => importeMedioReciboValidoParaEnvio(pago))
                .map(({ medioLabel, ...rest }) => rest);
            if (detallesValidos.length === 0) {
                enqueueSnackbar(
                    'Debe agregar al menos un medio de cobro con monto distinto de cero (en efectivo puede usar monto negativo para vuelto).',
                    { variant: 'warning' }
                );
                return;
            }

            const total = detallesValidos.reduce((sum, p) => sum + (p.importe || 0), 0);
            if (total <= 0) {
                enqueueSnackbar('El monto total debe ser mayor a 0', { variant: 'warning' });
                return;
            }

            // Determinar grupo MCP SOLO desde el Grupo de Caja seleccionado
            const storedGrupo = Number(localStorage.getItem('grupoCajaMcp') || 0);
            const grupoMCP = (grupoCajaSeleccionado?.codigo ?? (Number.isFinite(storedGrupo) && storedGrupo > 0 ? storedGrupo : undefined)) as number | undefined;
            if (!grupoMCP || !Number.isFinite(grupoMCP)) {
                enqueueSnackbar('Debe seleccionar un Grupo de Caja válido antes de continuar', { variant: 'warning' });
                setOpenGrupoCajaDialog(true);
                return;
            }

            // Validaciones adicionales de negocio y tipos antes de construir el payload
            const errores: string[] = [];
            // Encabezado
            if (!Number.isFinite(Number(formulario.nroCliente)) || Number(formulario.nroCliente) <= 0) {
                errores.push('Número de Cliente es obligatorio y debe ser numérico (>0)');
            }
            const ns = Number(formulario.nroSuc);
            if (!Number.isFinite(ns) || ns <= 0) {
                errores.push('Nº Sucursal es obligatorio y debe ser numérico (>0)');
            }

            // Detalles de pago
            detallesValidos.forEach((p, idx) => {
                const idxLabel = `Pago ${idx + 1}`;
                if (!p.mcp || String(p.mcp).trim() === '') errores.push(`${idxLabel}: Debe seleccionar el medio específico (MCP)`);
                const impP = Number(p.importe);
                const esEf = (p.tipoPago || '').toLowerCase() === 'efectivo';
                if (!Number.isFinite(impP) || impP === 0) errores.push(`${idxLabel}: Monto debe ser distinto de cero`);
                else if (!esEf && impP <= 0) errores.push(`${idxLabel}: Monto debe ser mayor a cero (solo efectivo admite vuelto negativo)`);

                const tipo = (p.tipoPago || '').toLowerCase();
                if (tipo === 'cheque') {
                    const nroBanco = Number(p.nroBanco);
                    const nroSucursal = Number(p.nroSucursal);
                    if (!Number.isFinite(nroBanco)) errores.push(`${idxLabel}: Banco inválido`);
                    if (!Number.isFinite(nroSucursal)) errores.push(`${idxLabel}: Nº Sucursal inválido`);
                    if (!p.nroCheque || String(p.nroCheque).trim() === '') errores.push(`${idxLabel}: Nº Cheque es obligatorio`);
                    if (!p.librador || String(p.librador).trim() === '') errores.push(`${idxLabel}: Librador es obligatorio`);
                    // Fechas
                    if (!p.fechaEmision || isNaN(new Date(p.fechaEmision as any).getTime())) errores.push(`${idxLabel}: Fecha de Emisión inválida`);
                    if (!p.fechaVencimiento || isNaN(new Date(p.fechaVencimiento as any).getTime())) errores.push(`${idxLabel}: Fecha de Vencimiento inválida`);
                }
                if (tipo === 'tarjeta') {
                    const codMarca = Number(p.codigoMarca);
                    if (!Number.isFinite(codMarca) || codMarca <= 0) errores.push(`${idxLabel}: Marca es obligatoria`);
                    if (!p.numeroroAutorizacion || String(p.numeroroAutorizacion).trim() === '') {
                        errores.push(`${idxLabel}: Ingresá el número de autorización.`);
                    }
                }
                if (tipo === 'transferencia') {
                    if (!p.mcp || String(p.mcp).trim() === '') errores.push(`${idxLabel}: Debe seleccionar el medio de transferencia (MCP)`);
                    if (p.carteraRequerida && (!p.cartera || Number(p.cartera) < 1000)) {
                        errores.push(`${idxLabel}: Debe seleccionar la cartera de recepción`);
                    }
                }
            });

            if (errores.length > 0) {
                enqueueSnackbar(errores.join(' | '), { variant: 'error' });
                return;
            }

            // Construir mediosDeCobro según el endpoint requerido
            const mediosDeCobro = detallesValidos.map((p) => {
                const esCheque = (p.tipoPago || '').toLowerCase() === 'cheque';
                const base: any = {
                    mcp: p.mcp || '',
                    importe: p.importe,
                    // "detalle" siempre sale de Observaciones del formulario
                    detalle: (p.detalle || '')
                };
                if (esCheque) {
                    base.cheque = {
                        nroBanco: p.nroBanco ?? 0,
                        nroSucursal: p.nroSucursal ?? 0,
                        nroCheque: p.nroCheque ? formatNroCheque(p.nroCheque) : '',
                        nroLibrador: p.nroLibrador || '',
                        librador: p.librador || '',
                        fechaEmision: p.fechaEmision ? new Date(p.fechaEmision).toISOString() : new Date().toISOString(),
                        fechaVencimiento: p.fechaVencimiento ? new Date(p.fechaVencimiento).toISOString() : new Date().toISOString(),
                        ctaBancaria: p.ctaBancaria || '',
                        concepto: (p.concepto || '').slice(0, 30),
                        esElectronico: Boolean(p.esElectronico),
                        ...(p.bcraIngreso ? { bcraIngreso: p.bcraIngreso } : {}),
                    };
                }
                if ((p.tipoPago || '').toLowerCase() === 'tarjeta') {
                    base.tarjeta = {
                        codigoMarca: p.codigoMarca ?? 0,
                        nroTarjeta: p.nroTarjeta || '',
                        nroComprobanteTa: p.nroComprobanteTa ?? 0,
                        fecha: p.fecha ? new Date(p.fecha).toISOString() : new Date().toISOString(),
                        fechaPresentar: p.fechaPresentacion ? new Date(p.fechaPresentacion).toISOString() : new Date().toISOString(),
                        numeroroAutorizacion: p.numeroroAutorizacion || '',
                        cuotas: p.cuotas ?? 0,
                        telefono: p.telefono || '',
                        titularTarjeta: p.titular || ''
                    };
                }
                if ((p.tipoPago || '').toLowerCase() === 'transferencia' && p.carteraRequerida && p.cartera != null && Number(p.cartera) >= 1000) {
                    base.cartera = p.cartera;
                }
                if ((p.tipoPago || '').toLowerCase() === 'otros') {
                    const fr = (p.fechaRegistro || '').trim();
                    if (fr) {
                        base.fecha = new Date(`${fr}T12:00:00`).toISOString();
                    }
                    const nro = Number(p.nroComprobante);
                    if (Number.isFinite(nro) && nro > 0) {
                        base.nroComprobante = Math.trunc(nro);
                    }
                }
                return base;
            });

            // Construir documentosAplicar: distribuir Total Medios entre comprobantes seleccionados
            let documentosAplicar: { nroIdformulario: number; montoCancelado: number }[] = [];
            if (mediosDeCobro.length > 0 && total > 0 && comprobantesSeleccionados.size > 0) {
                // Iterar en el orden actual de los comprobantes y aplicar a los seleccionados
                let restante = total;
                for (const comp of comprobantesDeuda) {
                    if (restante <= 0) break;
                    if (!comprobantesSeleccionados.has(comp.id)) continue;
                    if (!esComprobanteImputableRecibo(comp)) continue;
                    if (!comp.nroFormulario) continue; // Solo documentos aplicables

                    const saldoDisponible = Number(comp.saldo ?? comp.importe ?? 0) || 0;
                    if (saldoDisponible <= 0) continue;

                    // Si el usuario ingresó un monto específico en "Aplicar", respetarlo hasta saldo/restante
                    const montoDeseado = montosAplicar.get(comp.id);
                    let aAplicar = Number.isFinite(Number(montoDeseado)) ? Number(montoDeseado) : saldoDisponible;
                    aAplicar = Math.max(0, Math.min(aAplicar, saldoDisponible, restante));

                    if (aAplicar > 0) {
                        documentosAplicar.push({ nroIdformulario: comp.nroFormulario, montoCancelado: aAplicar });
                        restante -= aAplicar;
                    }
                }
                // Si no se pudo aplicar nada (p. ej. sin nroFormulario o saldos 0), dejamos documentosAplicar vacío
            }

            console.log('📋 Documentos a aplicar a enviar:', documentosAplicar);

            // Tomar sucursal desde el formulario (si no viene, usar 0)
            const nroSucValue: number = Number(formulario.nroSuc) || 0;
            const payload = {
                nroCliente: formulario.nroCliente,
                nroSuc: nroSucValue,
                grupoMCP,
                // Cabecera: respetar la fecha elegida por el usuario
                fechaEmision: formulario.fechaEmision,
                descripcion: formulario.descripcion || '',
                mediosDeCobro,
                documentosAplicar: documentosAplicar.length > 0 ? documentosAplicar : undefined
            };

            console.log('📤 Payload completo a enviar:', payload);

            try {
                const response = await api.post('/api/Tesoreria/recibos', payload);
                console.log('✅ Respuesta del servidor:', response.data);
                
                // Actualizar los saldos de los documentos aplicados
                if (documentosAplicar.length > 0) {
                    // Capturar los datos necesarios antes de actualizar el estado
                    const idsSeleccionados = Array.from(comprobantesSeleccionados);
                    const comprobantesActuales = [...comprobantesDeuda];
                    
                    console.log('🔄 Iniciando actualización de saldos...');
                    console.log('📋 Documentos aplicados:', documentosAplicar);
                    console.log('📋 IDs seleccionados:', idsSeleccionados);
                    console.log('📋 Comprobantes deuda actuales:', comprobantesActuales.map(c => ({
                        id: c.id,
                        nroComprobante: c.nroComprobante,
                        nroFormulario: c.nroFormulario,
                        saldo: c.saldo
                    })));
                    
                    // Crear un mapa de documentos aplicados por nroFormulario para búsqueda rápida
                    const docsAplicadosMap = new Map<number, number>();
                    documentosAplicar.forEach(doc => {
                        docsAplicadosMap.set(doc.nroIdformulario, doc.montoCancelado);
                    });
                    
                    setComprobantesDeuda(prevComprobantes => {
                        const comprobantesActualizados = prevComprobantes.map(comp => {
                            // Verificar si este comprobante fue seleccionado
                            const fueSeleccionado = idsSeleccionados.includes(comp.id);
                            
                            if (fueSeleccionado && comp.nroFormulario) {
                                console.log(`🔍 Verificando comprobante seleccionado: ${comp.nroComprobante} (ID: ${comp.id}, nroFormulario: ${comp.nroFormulario})`);
                                
                                // Buscar el monto cancelado para este comprobante
                                const montoCancelado = docsAplicadosMap.get(comp.nroFormulario);
                                
                                if (montoCancelado !== undefined) {
                                    // Descontar el monto pagado del saldo
                                    const saldoAnterior = comp.saldo || 0;
                                    const nuevoSaldo = Math.max(0, saldoAnterior - montoCancelado);
                                    
                                    console.log(`✅ Descontando pago de factura ${comp.nroComprobante}:`, {
                                        saldoAnterior: saldoAnterior,
                                        montoCancelado: montoCancelado,
                                        nuevoSaldo: nuevoSaldo,
                                        diferencia: saldoAnterior - nuevoSaldo
                                    });
                                    
                                    return {
                                        ...comp,
                                        saldo: nuevoSaldo
                                    };
                                } else {
                                    console.warn(`⚠️ No se encontró monto cancelado para comprobante ${comp.nroComprobante} (nroFormulario: ${comp.nroFormulario})`);
                                    console.warn(`   Documentos aplicados disponibles:`, Array.from(docsAplicadosMap.entries()).map(([nroId, monto]) => ({
                                        nroIdformulario: nroId,
                                        montoCancelado: monto
                                    })));
                                }
                            }
                            
                            return comp;
                        }).filter(comp =>
                            esNcVisibleEnRecibo(comp, null) || (comp.saldo || 0) > 0
                        ); // Mantener NC; eliminar débitos con saldo 0
                        
                        // Recalcular saldos totales solo con débitos
                        const debitosActualizados = comprobantesActualizados.filter((c) =>
                            esComprobanteImputableRecibo(c)
                        );
                        const total = debitosActualizados.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        const comprobantesVencidos = debitosActualizados.filter(c => c.vencido === true);
                        const vencido = comprobantesVencidos.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        const comprobantesVigentes = debitosActualizados.filter(c => c.vencido === false);
                        const vigente = comprobantesVigentes.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        
                        setSaldoTotal(total);
                        setSaldoVencido(vencido);
                        setSaldoVigente(vigente);
                        
                        console.log('💰 Saldos actualizados después del pago:', {
                            saldoTotal: total,
                            saldoVencido: vencido,
                            saldoVigente: vigente,
                            comprobantesActualizados: comprobantesActualizados.length
                        });
                        
                        return comprobantesActualizados;
                    });
                    
                    // Limpiar selecciones y montos a aplicar DESPUÉS de actualizar los comprobantes
                    setTimeout(() => {
                        setComprobantesSeleccionados(new Set());
                        setMontosAplicar(new Map());
                    }, 100);
                }
                
                const cantidadFacturas = documentosAplicar.length;
                const mensaje = cantidadFacturas > 0 
                    ? `Pago procesado exitosamente. ${cantidadFacturas} factura${cantidadFacturas > 1 ? 's' : ''} pagada${cantidadFacturas > 1 ? 's' : ''}.`
                    : 'Recibo creado exitosamente';
                
                enqueueSnackbar(mensaje, { variant: 'success' });
                
                // Esperar un momento para que la actualización se refleje antes de navegar
                setTimeout(() => {
                    navigate('/tesoreria/recibos');
                }, 500);
            } catch (apiError: any) {
                // Interpretar errores del servidor y mostrarlos claramente
                const status = apiError?.response?.status;
                const data = apiError?.response?.data;
                if (data?.errors && typeof data.errors === 'object') {
                    try {
                        const msgs: string[] = [];
                        for (const k of Object.keys(data.errors)) {
                            const val = data.errors[k];
                            if (Array.isArray(val)) msgs.push(`${k}: ${val.join(' - ')}`);
                            else if (val) msgs.push(`${k}: ${String(val)}`);
                        }
                        enqueueSnackbar(msgs.join(' | ') || data.title || 'Error de validación', { variant: 'error' });
                        return;
                    } catch {}
                }
                if (status === 400) {
                    enqueueSnackbar(data?.title || 'Solicitud inválida (400). Revise los datos ingresados.', { variant: 'error' });
                    return;
                }
                if (status === 403) {
                    notifyPermissionDenied(enqueueSnackbar, apiError);
                    return;
                }
                if (status === 401) {
                    enqueueSnackbar('No autorizado para crear recibos. Verifique su sesión.', { variant: 'error' });
                    return;
                }
                if (status === 404) {
                    enqueueSnackbar('Endpoint no encontrado (404). Consulte con soporte.', { variant: 'error' });
                    return;
                }
                if (status === 500) {
                    enqueueSnackbar('Error interno del servidor (500). Intente nuevamente.', { variant: 'error' });
                    return;
                }
                throw apiError; // fallback
            }
        } catch (error: any) {
            console.error('Error:', error);
            const data = error?.response?.data;
            if (data?.errors && typeof data.errors === 'object') {
                try {
                    const msgs: string[] = [];
                    for (const k of Object.keys(data.errors)) {
                        const val = data.errors[k];
                        if (Array.isArray(val)) {
                            msgs.push(`${k}: ${val.join(' - ')}`);
                        } else if (val) {
                            msgs.push(`${k}: ${String(val)}`);
                        }
                    }
                    const message = msgs.join(' | ');
                    enqueueSnackbar(message || data.title || 'Error de validación', { variant: 'error' });
                    return;
                } catch {}
            }
            enqueueSnackbar(data?.title || data || 'Error al crear el recibo', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
            <Card 
                sx={{ 
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tesoreria/recibos')} size="small" sx={{ textTransform: 'none' }}>
                            Recibos
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Typography
                            variant={isSmallScreen ? 'h5' : 'h4'}
                            sx={{
                                fontWeight: 600,
                                color: '#1976d2',
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                            }}
                        >
                            Nuevo Recibo
                        </Typography>
                    </Stack>
                    <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                display="block"
                                                mb={0.75}
                                                sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                            >
                                                Cliente
                                            </Typography>
                                            {!(formulario.nroCliente > 0 && formulario.nombre) ? (
                                                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                                        <ClienteInlineSearchField
                                                            label=""
                                                            placeholder="Nº cliente, nombre, razón social o CUIT"
                                                            helperText=""
                                                            excluirInactivos
                                                            disabled={clienteBloqueado}
                                                            onSelect={handleClienteSelect}
                                                        />
                                                    </Box>
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<PersonSearch color="primary" />}
                                                        disabled={clienteBloqueado}
                                                        onClick={() => setOpenClienteSearch(true)}
                                                        sx={{
                                                            py: 1,
                                                            px: 2,
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            whiteSpace: 'nowrap',
                                                            flexShrink: 0,
                                                            bgcolor: 'background.paper',
                                                            borderColor: 'divider',
                                                            '&:hover': {
                                                                bgcolor: 'action.hover',
                                                                borderColor: 'primary.main',
                                                            },
                                                        }}
                                                    >
                                                        Seleccionar cliente
                                                    </Button>
                                                </Stack>
                                            ) : (
                                                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                            <Box flex={1} minWidth={0}>
                                                                <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap" useFlexGap>
                                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                                        {formulario.nombre}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        #{formulario.nroCliente}
                                                                    </Typography>
                                                                </Stack>
                                                                {formulario.cuit ? (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        CUIT: {formulario.cuit}
                                                                    </Typography>
                                                                ) : null}
                                                                {formulario.tipoIVA ? (
                                                                    <Box mt={0.75}>
                                                                        <Chip
                                                                            label={TIPO_IVA_LABELS[formulario.tipoIVA] ?? formulario.tipoIVA}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ height: 22, fontSize: '0.7rem' }}
                                                                        />
                                                                    </Box>
                                                                ) : null}
                                                            </Box>
                                                            {!clienteBloqueado && (
                                                                <Stack direction="row" spacing={0} flexShrink={0}>
                                                                    <Tooltip title="Cambiar cliente">
                                                                        <IconButton size="small" onClick={() => setOpenClienteSearch(true)} aria-label="Cambiar cliente">
                                                                            <Edit fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Quitar cliente">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={handleClienteClear}
                                                                            aria-label="Quitar cliente"
                                                                        >
                                                                            <DeleteOutline fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            )}
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            )}
                                            {clienteBloqueado && (
                                                <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                                                    No se puede modificar el cliente cuando hay comprobantes aplicados o medios de cobro asignados
                                                </Typography>
                                            )}
                                        </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    p: 2,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    textTransform="uppercase"
                                    letterSpacing={0.5}
                                    display="block"
                                    mb={1.5}
                                >
                                    Datos de emisión
                                </Typography>
                                <Stack spacing={1.5}>
                                    <SearchableSelect
                                        label="Sucursal"
                                        size="small"
                                        required
                                        loading={loadingSucursales}
                                        options={sucursales.map((sucursal) => ({
                                            value: sucursal.nroSuc,
                                            label: sucursal.nombreSucursal?.trim()
                                                ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                                                : `Suc. ${sucursal.nroSuc}`,
                                        }))}
                                        value={formulario.nroSuc > 0 ? formulario.nroSuc : null}
                                        onChange={(value) => onSucursalChange(Number(value) || 0)}
                                    />
                                    <GrupoCajaSelect
                                        usuario={loggedUsername}
                                        value={grupoCajaSeleccionado}
                                        onChange={handleGrupoCajaChange}
                                        locked={grupoCajaBloqueado}
                                        required
                                    />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Fecha de Emisión"
                                        name="fechaEmision"
                                        type="date"
                                        value={formulario.fechaEmision}
                                        onChange={handleFormularioChange}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Observaciones"
                                        name="descripcion"
                                        value={formulario.descripcion}
                                        onChange={handleFormularioChange}
                                        multiline
                                        minRows={2}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                                    <NotesIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 1 }} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                        <Card sx={{ boxShadow: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    display="block"
                                    mb={1.5}
                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                >
                                    Comprobantes adeudados
                                </Typography>
                            {/* Información de Saldos */}
                            <Box sx={{ mb: 3, bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                Vigente:
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: saldoVigente < 0 ? 'success.main' : 'text.primary' }}>
                                                {formatCurrency(saldoVigente)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                Vencido:
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: saldoVencido < 0 ? 'success.main' : 'error.main' }}>
                                                {formatCurrency(saldoVencido)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                Saldo Total:
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'white',
                                                    bgcolor: saldoTotal < 0 ? 'success.main' : 'error.main',
                                                    borderRadius: 1.5,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    display: 'inline-block',
                                                    minWidth: '170px',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                {formatCurrency(saldoTotal)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Sección de Comprobantes Seleccionados */}
                            {comprobantesSeleccionados.size > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Card sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 2, boxShadow: 2 }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                                        Comprobantes Seleccionados
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {comprobantesSeleccionados.size} comprobante{comprobantesSeleccionados.size > 1 ? 's' : ''}
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                                        Total: {formatCurrency(totalAplicado)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setComprobantesSeleccionados(new Set());
                                                            setMontosAplicar(new Map());
                                                            enqueueSnackbar('Comprobantes deseleccionados', { variant: 'info' });
                                                        }}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}

                            {/* Barra de búsqueda y botón Pagar Todo */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField
                                    fullWidth
                                    placeholder="Buscar comprobantes..."
                                    value={searchComprobante}
                                    onChange={(e) => setSearchComprobante(e.target.value)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ 
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: 'grey.100'
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handlePagarTodo}
                                    sx={{ 
                                        minWidth: { xs: '100%', sm: '150px' },
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600
                                    }}
                                    disabled={
                                        comprobantesDeuda.filter((c) => esComprobanteImputableRecibo(c)).length === 0 ||
                                        saldoTotal === 0
                                    }
                                >
                                    Pagar Todo
                                </Button>
                            </Box>

                            {/* Lista de Comprobantes */}
                            {loadingComprobantes ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Cargando comprobantes...
                                    </Typography>
                                    {formulario.nroCliente > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Cliente: {formulario.nroCliente} - {formulario.nombre || 'Sin nombre'}
                                        </Typography>
                                    )}
                                </Box>
                            ) : comprobantesDeuda.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        No hay comprobantes disponibles
                                    </Typography>
                                    {formulario.nroCliente > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Cliente: {formulario.nroCliente}
                                        </Typography>
                                    )}
                                    {formulario.nroCliente === 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Seleccione un cliente para ver sus comprobantes de deuda
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {comprobantesDeuda
                                        .filter(comp => 
                                            !searchComprobante || 
                                            comp.nroComprobante.toLowerCase().includes(searchComprobante.toLowerCase()) ||
                                            comp.tipoComprobante.toLowerCase().includes(searchComprobante.toLowerCase())
                                        )
                                        .sort((a, b) => {
                                            const aNc = esNcVisibleEnRecibo(a, null);
                                            const bNc = esNcVisibleEnRecibo(b, null);
                                            if (aNc !== bNc) return aNc ? 1 : -1;

                                            // Ordenar de mayor tiempo vencido a menor tiempo vencido
                                            const hoy = new Date();
                                            hoy.setHours(0, 0, 0, 0); // Normalizar a medianoche
                                            
                                            const fechaVencA = new Date(a.fechaVencimiento);
                                            fechaVencA.setHours(0, 0, 0, 0);
                                            const fechaVencB = new Date(b.fechaVencimiento);
                                            fechaVencB.setHours(0, 0, 0, 0);
                                            
                                            const estaVencidaA = fechaVencA < hoy;
                                            const estaVencidaB = fechaVencB < hoy;
                                            
                                            // Si ambas están vencidas, ordenar por tiempo vencido (mayor tiempo vencido primero)
                                            if (estaVencidaA && estaVencidaB) {
                                                const tiempoVencidoA = hoy.getTime() - fechaVencA.getTime();
                                                const tiempoVencidoB = hoy.getTime() - fechaVencB.getTime();
                                                // Orden descendente: mayor tiempo vencido primero
                                                return tiempoVencidoB - tiempoVencidoA;
                                            }
                                            
                                            // Si solo A está vencida, A va primero
                                            if (estaVencidaA && !estaVencidaB) {
                                                return -1;
                                            }
                                            
                                            // Si solo B está vencida, B va primero
                                            if (!estaVencidaA && estaVencidaB) {
                                                return 1;
                                            }
                                            
                                            // Si ninguna está vencida, ordenar por fecha de vencimiento (más lejanas primero)
                                            return fechaVencB.getTime() - fechaVencA.getTime();
                                        })
                                        .map((comp, idx, arr) => {
                                            const esNc = esNcVisibleEnRecibo(comp, null);
                                            const esImputable = esComprobanteImputableRecibo(comp);
                                            const estaSeleccionado = !esNc && comprobantesSeleccionados.has(comp.id);
                                            const estaExpandido = !esNc && comprobantesExpandidos.has(comp.id);
                                            const numeroFormateado = formatNumeroComprobante(comp.nroComprobante, comp.tipoComprobante);
                                            // Saldo acumulado de línea: solo débitos imputables
                                            const saldoAcumuladoLinea = arr
                                                .slice(0, idx + 1)
                                                .filter((it) => esComprobanteImputableRecibo(it))
                                                .reduce((sum, it) => sum + (Number(it.saldo ?? it.importe ?? 0) || 0), 0);

                                            if (esNc) {
                                                return (
                                                    <Card
                                                        key={comp.id}
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: 2,
                                                            bgcolor: 'rgba(46, 125, 50, 0.04)',
                                                            borderWidth: 2,
                                                            borderStyle: 'solid',
                                                            borderColor: 'success.light',
                                                            '&:hover': { boxShadow: 2 },
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, position: 'relative' }}>
                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.25, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                                                                <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 2 }, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                                                                    <Box sx={{ width: 28, flexShrink: 0 }} />
                                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: { xs: 0.75, sm: 0 } }}>
                                                                            <Typography
                                                                                variant="h6"
                                                                                sx={{
                                                                                    fontWeight: 700,
                                                                                    color: 'success.main',
                                                                                    mb: 0,
                                                                                    lineHeight: 1.2,
                                                                                    fontSize: { xs: '1rem', sm: '1.25rem' },
                                                                                    wordBreak: 'break-word',
                                                                                }}
                                                                            >
                                                                                {numeroFormateado}
                                                                            </Typography>
                                                                            <Chip
                                                                                label="NC"
                                                                                size="small"
                                                                                sx={{
                                                                                    bgcolor: 'success.main',
                                                                                    color: 'white',
                                                                                    fontWeight: 600,
                                                                                    height: 24,
                                                                                    fontSize: '0.75rem',
                                                                                    borderRadius: '12px',
                                                                                    px: 1,
                                                                                    flexShrink: 0,
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.35, sm: 1 }, flexWrap: 'wrap' }}>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                                                {comp.tipoComprobante.includes(' - ')
                                                                                    ? comp.tipoComprobante
                                                                                    : `${comp.nroTipoFormulario || '3'} - Nota de Crédito`}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                {new Date(comp.fechaEmision).toLocaleDateString('es-AR', {
                                                                                    day: '2-digit',
                                                                                    month: '2-digit',
                                                                                    year: 'numeric',
                                                                                })}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ mt: 1.5 }}>
                                                                            <Button
                                                                                variant="outlined"
                                                                                color="success"
                                                                                size="small"
                                                                                startIcon={<AccountBalanceWalletIcon />}
                                                                                onClick={() => handleAbrirAplicarNc(comp)}
                                                                                disabled={!comp.nroFormulario}
                                                                                sx={{
                                                                                    borderRadius: 2,
                                                                                    textTransform: 'none',
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                Aplicar
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display: { xs: 'flex', sm: 'block' },
                                                                        justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                                                        alignItems: { xs: 'center', sm: 'flex-start' },
                                                                        textAlign: { xs: 'left', sm: 'right' },
                                                                        minWidth: { xs: 'auto', sm: '110px' },
                                                                        width: { xs: '100%', sm: 'auto' },
                                                                        flexShrink: 0,
                                                                        borderTop: { xs: '1px solid', sm: 'none' },
                                                                        borderColor: 'divider',
                                                                        pt: { xs: 1.25, sm: 0 },
                                                                        mt: { xs: 0.25, sm: 0 },
                                                                        pl: { xs: 4.5, sm: 0 },
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: '0.75rem', display: { xs: 'inline', sm: 'none' } }}
                                                                    >
                                                                        Saldo
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            color: 'success.main',
                                                                            mb: { xs: 0, sm: 0.5 },
                                                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                                                        }}
                                                                    >
                                                                        {formatCurrency(Math.abs(comp.saldo || 0))}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ fontSize: '0.75rem', display: { xs: 'none', sm: 'block' } }}
                                                                    >
                                                                        Saldo a favor
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                );
                                            }
                                            
                                            return (
                                                <Card 
                                                    key={comp.id}
                                                    variant="outlined" 
                                                    sx={{ 
                                                        borderRadius: 2,
                                                        bgcolor: 'white',
                                                        borderWidth: 2,
                                                        borderStyle: 'solid',
                                                        borderColor: estaSeleccionado ? 'primary.main' : 'divider',
                                                        '&:hover': {
                                                            boxShadow: 2
                                                        }
                                                    }}
                                                >
                                                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, position: 'relative' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.25, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                                                        <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 2 }, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                                                            <Checkbox
                                                                checked={estaSeleccionado}
                                                                onChange={() => handleToggleComprobante(comp.id)}
                                                                disabled={!esImputable}
                                                                sx={{
                                                                    p: 0,
                                                                    mt: 0.25,
                                                                    flexShrink: 0,
                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: 28
                                                                    }
                                                                }}
                                                                icon={<RadioButtonUncheckedIcon sx={{ color: 'grey.400' }} />}
                                                                checkedIcon={<RadioButtonCheckedIcon sx={{ color: 'primary.main' }} />}
                                                            />
                                                            
                                                            {/* Contenido del comprobante */}
                                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                {/* Encabezado: nro + estado */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: { xs: 0.75, sm: 0 } }}>
                                                                    <Typography 
                                                                        variant="h6" 
                                                                        sx={{ 
                                                                            fontWeight: 700, 
                                                                            color: estaSeleccionado ? 'success.main' : 'text.primary',
                                                                            mb: 0,
                                                                            lineHeight: 1.2,
                                                                            fontSize: { xs: '1rem', sm: '1.25rem' },
                                                                            wordBreak: 'break-word',
                                                                        }}
                                                                    >
                                                                        {numeroFormateado}
                                                                    </Typography>

                                                                    {comp.vencido ? (
                                                                        <Chip
                                                                            label="Vencido"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: 'error.main',
                                                                                color: 'white',
                                                                                fontWeight: 600,
                                                                                height: 24,
                                                                                fontSize: '0.75rem',
                                                                                borderRadius: '12px',
                                                                                px: 1,
                                                                                flexShrink: 0,
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Chip
                                                                            label="Vigente"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: 'success.main',
                                                                                color: 'white',
                                                                                fontWeight: 600,
                                                                                height: 24,
                                                                                fontSize: '0.75rem',
                                                                                borderRadius: '12px',
                                                                                px: 1,
                                                                                flexShrink: 0,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>

                                                                {/* Meta: tipo + fecha (+ ver detalles en desktop en la misma fila) */}
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                                        alignItems: { xs: 'stretch', sm: 'center' },
                                                                        gap: { xs: 0.35, sm: 1 },
                                                                        flexWrap: 'wrap',
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                                        {comp.tipoComprobante.includes(' - ') 
                                                                            ? comp.tipoComprobante 
                                                                            : `${comp.id || comp.tipoComprobante.substring(0, 1)} - ${comp.tipoComprobante}`}
                                                                    </Typography>

                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {new Date(comp.fechaEmision).toLocaleDateString('es-AR', {
                                                                            day: '2-digit',
                                                                            month: '2-digit',
                                                                            year: 'numeric'
                                                                        })}
                                                                    </Typography>

                                                                    <Button
                                                                        size="small"
                                                                        endIcon={<ExpandMoreIcon sx={{ 
                                                                            transform: estaExpandido ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                            transition: 'transform 0.3s',
                                                                            fontSize: '1rem'
                                                                        }} />}
                                                                        onClick={() => handleToggleDetalles(comp.id)}
                                                                        sx={{
                                                                            color: 'primary.main',
                                                                            textTransform: 'none',
                                                                            p: 0.5,
                                                                            px: { xs: 0, sm: 0.5 },
                                                                            minWidth: 'auto',
                                                                            alignSelf: { xs: 'flex-start', sm: 'center' },
                                                                            fontSize: '0.875rem',
                                                                            fontWeight: 500,
                                                                            mt: { xs: 0.25, sm: 0 },
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(25, 118, 210, 0.08)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {estaExpandido ? 'Ver menos' : 'Ver detalles'}
                                                                    </Button>
                                                                </Box>
                                                                
                                                                {/* Detalles expandidos */}
                                                                {estaExpandido && (
                                                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                        {/* Importe */}
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                Importe:
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                {formatCurrency(comp.importe)}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Vencimiento */}
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                Vencimiento:
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                {comp.fechaVencimiento ? new Date(comp.fechaVencimiento).toLocaleDateString('es-AR', {
                                                                                    day: '2-digit',
                                                                                    month: '2-digit',
                                                                                    year: 'numeric'
                                                                                }) : 'N/A'}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Aplicar - Solo se muestra si la factura está seleccionada */}
                                                                        {estaSeleccionado && (
                                                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.5, sm: 0 }, mb: 1.5 }}>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                    Aplicar:
                                                                                </Typography>
                                                                                <TextField
                                                                                    type="number"
                                                                                    size="small"
                                                                                    fullWidth={isSmallScreen}
                                                                                    value={montosAplicar.get(comp.id) ?? ''}
                                                                                    onChange={(e) => {
                                                                                        const valorIngresado = e.target.value;
                                                                                        
                                                                                        // Si el campo está vacío, permitir que se borre completamente
                                                                                        if (valorIngresado === '' || valorIngresado === null || valorIngresado === undefined) {
                                                                                            setMontosAplicar(prev => {
                                                                                                const nuevo = new Map(prev);
                                                                                                nuevo.delete(comp.id);
                                                                                                return nuevo;
                                                                                            });
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        // Convertir a número
                                                                                        const nuevoMonto = parseFloat(valorIngresado);
                                                                                        
                                                                                        // Si no es un número válido, no actualizar
                                                                                        if (isNaN(nuevoMonto)) {
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        const importeMaximo = comp.importe || 0;
                                                                                        
                                                                                        // Si el monto ingresado es mayor al importe, no actualizar y mostrar advertencia
                                                                                        if (nuevoMonto > importeMaximo) {
                                                                                            enqueueSnackbar(`El monto a aplicar no puede ser mayor al importe (${formatCurrency(importeMaximo)})`, { variant: 'warning' });
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        // Si el monto es válido (menor o igual al importe), actualizarlo
                                                                                        setMontosAplicar(prev => {
                                                                                            const nuevo = new Map(prev);
                                                                                            nuevo.set(comp.id, roundMoney(nuevoMonto));
                                                                                            return nuevo;
                                                                                        });
                                                                                    }}
                                                                                    onBlur={() => {
                                                                                        // Validar al perder el foco
                                                                                        const valorActual = montosAplicar.get(comp.id);
                                                                                        const importeMaximo = comp.importe || 0;
                                                                                        
                                                                                        // Si el campo está vacío, dejarlo vacío
                                                                                        if (valorActual === undefined || valorActual === null) {
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        // Si el valor es mayor al importe, establecerlo al máximo permitido
                                                                                        if (valorActual > importeMaximo) {
                                                                                            setMontosAplicar(prev => {
                                                                                                const nuevo = new Map(prev);
                                                                                                nuevo.set(comp.id, roundMoney(importeMaximo));
                                                                                                return nuevo;
                                                                                            });
                                                                                            enqueueSnackbar(`El monto se ajustó al máximo permitido: ${formatCurrency(importeMaximo)}`, { variant: 'info' });
                                                                                        }
                                                                                    }}
                                                                                    inputProps={{
                                                                                        min: 0,
                                                                                        max: comp.importe || 0,
                                                                                        step: 0.01
                                                                                    }}
                                                                                    placeholder="0"
                                                                                    sx={{
                                                                                        width: { xs: '100%', sm: '150px' },
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 1
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {/* Saldo Acumulado - acumulado progresivo por línea (responsive) */}
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                flexDirection: { xs: 'column', sm: 'row' },
                                                                                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                                                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                                                mb: 2,
                                                                                gap: { xs: 0.25, sm: 0 },
                                                                                width: '100%'
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                Saldo Acumulado:
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color={saldoAcumuladoLinea < 0 ? 'success.main' : 'error.main'}
                                                                                sx={{
                                                                                    fontWeight: 700,
                                                                                    textAlign: { xs: 'left', sm: 'right' },
                                                                                    mt: { xs: 0.25, sm: 0 },
                                                                                    width: { xs: '100%', sm: 'auto' },
                                                                                    overflowWrap: 'anywhere',
                                                                                    wordBreak: 'break-word'
                                                                                }}
                                                                            >
                                                                                {formatCurrency(saldoAcumuladoLinea)}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Botón Detalles */}
                                                                        <Box sx={{ mt: 2 }}>
                                                                            <Button
                                                                                variant="contained"
                                                                                color="primary"
                                                                                startIcon={<DescriptionIcon />}
                                                                                onClick={() => handleVerDetalles(comp.nroFormulario)}
                                                                                disabled={!comp.nroFormulario}
                                                                                sx={{
                                                                                    borderRadius: 2,
                                                                                    textTransform: 'none',
                                                                                    fontWeight: 600,
                                                                                    px: 3,
                                                                                    py: 1,
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                Detalles
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                            
                                                            {/* Monto a la derecha (debajo en mobile, con separador) */}
                                                            <Box sx={{
                                                                display: { xs: 'flex', sm: 'block' },
                                                                justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                                                alignItems: { xs: 'center', sm: 'flex-start' },
                                                                textAlign: { xs: 'left', sm: 'right' },
                                                                minWidth: { xs: 'auto', sm: '110px' },
                                                                width: { xs: '100%', sm: 'auto' },
                                                                flexShrink: 0,
                                                                borderTop: { xs: '1px solid', sm: 'none' },
                                                                borderColor: 'divider',
                                                                pt: { xs: 1.25, sm: 0 },
                                                                mt: { xs: 0.25, sm: 0 },
                                                                pl: { xs: 4.5, sm: 0 },
                                                            }}>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="text.secondary"
                                                                    sx={{ fontSize: '0.75rem', display: { xs: 'inline', sm: 'none' } }}
                                                                >
                                                                    Saldo
                                                                </Typography>
                                                                <Typography 
                                                                    variant="h6" 
                                                                    sx={{ 
                                                                        fontWeight: 700, 
                                                                        color: comp.saldo < 0 ? 'success.main' : 'error.main',
                                                                        mb: { xs: 0, sm: 0.5 },
                                                                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                                                    }}
                                                                >
                                                                    {formatCurrency(comp.saldo)}
                                                                </Typography>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="text.secondary"
                                                                    sx={{ fontSize: '0.75rem', display: { xs: 'none', sm: 'block' } }}
                                                                >
                                                                    Saldo
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                </Box>
                            )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ boxShadow: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    display="block"
                                    mb={1.5}
                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                >
                                    Medios de cobro
                                </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenPagoDialog}
                                disabled={!(Number(formulario.nroCliente) > 0) || !formulario.nombre}
                                fullWidth={isSmallScreen}
                            >
                                Agregar Pago
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            {detallesPago.map((pago, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>
                                                        {pago.tipoPago?.toUpperCase()}
                                                    </Typography>
                                                    {pago.tipoPago === 'Transferencia' && (
                                                        <>
                                                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                                {pago.medioLabel || pago.mcp || ''}
                                                            </Typography>
                                                            {!!pago.carteraLabel && (
                                                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                                                    Cartera: {pago.carteraLabel}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    )}
                                                    {pago.tipoPago === 'Efectivo' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            EFECTIVO CAJA CHICA
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Cheque' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            CAJA CHICA CHEQUES DE TERCERO
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Tarjeta' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {pago.medioLabel || 'CAJA TARJETA'}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Otros' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {pago.medioLabel || 'OTROS'}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Otros' && !!pago.fechaRegistro && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                            Fecha registro:{' '}
                                                            {new Date(`${pago.fechaRegistro}T12:00:00`).toLocaleDateString('es-AR')}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Otros' &&
                                                        pago.nroComprobante != null &&
                                                        pago.nroComprobante > 0 && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                            Nº comprobante: {pago.nroComprobante}
                                                        </Typography>
                                                    )}
                                                    {!!pago.detalle && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                            {pago.detalle}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 800, color: 'success.main' }}>
                                                        {formatCurrency(pago.importe || 0)}
                                                    </Typography>
                                                    <IconButton
                                                        onClick={() => removePago(index)}
                                                        size="small"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                            {detallesPago.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="body2">Aún no has agregado medios de pago.</Typography>
                                </Grid>
                            )}
                        </Grid>

                        {/* Resumen tipo tarjeta */}
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, borderLeft: '6px solid', borderColor: 'primary.main', bgcolor: 'background.paper' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography color="text.secondary">Total Aplicado:</Typography>
                                <Typography color="text.secondary">{formatCurrency(totalAplicado)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography color="text.secondary">Total Medios:</Typography>
                                <Typography sx={{ color: 'primary.main', fontWeight: 800 }}>{formatCurrency(totalMedios)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Diferencia:</Typography>
                                <Typography sx={{ 
                                    color: diferenciaInsuficiente ? 'error.main' : diferenciaSinSaldo ? 'success.main' : 'warning.main', 
                                    fontWeight: 800 
                                }}>
                                    {diferenciaInsuficiente 
                                        ? `- ${formatCurrency(Math.abs(diferencia))}` 
                                        : diferenciaSinSaldo 
                                            ? 'Sin Diferencia'
                                            : `${formatCurrency(diferencia)} A cuenta`}
                                </Typography>
                            </Box>
                            {diferenciaInsuficiente && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                                        Monto insuficiente. Falta: {formatCurrency(Math.abs(diferencia))}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="warning"
                                fullWidth
                                disabled={totalMedios <= 0 || diferenciaInsuficiente}
                                sx={{
                                    ...(diferenciaInsuficiente && {
                                        bgcolor: 'grey.400',
                                        color: 'text.secondary',
                                        '&:hover': {
                                            bgcolor: 'grey.400'
                                        }
                                    })
                                }}
                            >
                                {diferenciaInsuficiente 
                                    ? 'Monto Insuficiente' 
                                    : diferenciaSinSaldo 
                                        ? 'Procesar Pago' 
                                        : 'Procesar Pago a Cuenta'}
                            </Button>
                        </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Modal para agregar/editar pagos */}
                <Dialog 
                    open={openPagoDialog} 
                    onClose={handleClosePagoDialog}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={isSmallScreen}
                    onKeyDown={handlePagoDialogKeyDown}
                >
                    <DialogTitle>
                        Agregar Pago
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                                <Step>
                                    <StepLabel>Seleccionar Tipo</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Completar Datos</StepLabel>
                                </Step>
                            </Stepper>

                            {activeStep === 0 ? (
                                <SearchableSelect
                                    label="Tipo de Pago"
                                    options={[...TIPO_PAGO_OPTIONS]}
                                    value={tipoPagoSeleccionado || null}
                                    onChange={(value) => {
                                        if (value) void handleTipoPagoChange(value);
                                    }}
                                />
                            ) : (
                                renderCamposPago()
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClosePagoDialog}>
                            Cancelar
                        </Button>
                        {/* El botón Guardar solo se muestra en el último paso (activeStep === 1) */}
                        {activeStep === 1 ? (
                            <Button 
                                ref={guardarPagoBtnRef}
                                onClick={handleGuardarPago}
                                variant="contained"
                                disabled={!puedeGuardarPago}
                            >
                                Guardar
                            </Button>
                        ) : null}
                    </DialogActions>
                </Dialog>

                {/* Modal Detalle de Recibo */}
                <Dialog
                    open={openDetalleDialog}
                    onClose={() => setOpenDetalleDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {`Detalle de Recibo${detalleNroFormulario ? ` #${detalleNroFormulario}` : ''}`}
                    </DialogTitle>
                    <DialogContent>
                        {detalleLoading ? (
                            <Typography variant="body2">Cargando detalle...</Typography>
                        ) : detalleRecibo ? (
                            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                                {JSON.stringify(detalleRecibo, null, 2)}
                            </Box>
                        ) : (
                            <Typography variant="body2">No hay datos de detalle para mostrar.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDetalleDialog(false)}>Cerrar</Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de selección de Grupo de Caja Chica */}
                <Dialog
                    open={openGrupoCajaDialog}
                    onClose={() => setOpenGrupoCajaDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={isSmallScreen}
                >
                    <DialogTitle>Seleccionar Grupo de Caja</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleGrupoCajaScroll}>
                                <Grid container spacing={2}>
                                    {gruposCaja.map((g) => (
                                        <Grid item xs={12} key={g.id}>
                                            <Card
                                                variant={grupoCajaSeleccionadoId === g.id ? 'elevation' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => setGrupoCajaSeleccionadoId(g.id)}
                                            >
                                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <input
                                                        type="radio"
                                                        checked={grupoCajaSeleccionadoId === g.id}
                                                        onChange={() => setGrupoCajaSeleccionadoId(g.id)}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 600 }}>{g.nombre}</Typography>
                                                        <Typography variant="caption">Código: {g.codigo}</Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {gruposCaja.length === 0 && !grupoCajaLoading && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">No hay grupos de caja disponibles.</Typography>
                                        </Grid>
                                    )}
                                    {grupoCajaLoading && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">Cargando más grupos...</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenGrupoCajaDialog(false)}>Cancelar</Button>
                        <Button
                            onClick={() => void handleConfirmarGrupoCaja()}
                            variant="contained"
                            disabled={grupoCajaSeleccionadoId == null}
                        >
                            Seleccionar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de selección de Banco */}
                <Dialog
                    open={openBancoDialog}
                    onClose={() => setOpenBancoDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={isSmallScreen}
                >
                    <DialogTitle>Seleccionar Banco</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                placeholder="Buscar banco..."
                                value={bancoSearch}
                                onChange={(e) => setBancoSearch(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                        <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
                            <Grid container spacing={1}>
                                {bancos.map((banco, idx) => (
                                    <Grid item xs={12} key={banco.id ?? idx}>
                                        <Card
                                            variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setBancoSeleccionadoId(banco.id)}
                                        >
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="radio"
                                                    checked={bancoSeleccionadoId === banco.id}
                                                    onChange={() => setBancoSeleccionadoId(banco.id)}
                                                    style={{ marginRight: 8 }}
                                                />
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {!bancos.length && !bancoLoading && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No se encontraron bancos.</Typography>
                                    </Grid>
                                )}
                                {bancoLoading && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">Cargando bancos...</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenBancoDialog(false)}>Cancelar</Button>
                        <Button
                            variant="contained"
                            disabled={bancoSeleccionadoId == null}
                            onClick={() => {
                                const banco = bancos.find(b => b.id === bancoSeleccionadoId);
                                if (banco) {
                                    const label = buildBancoLabel(banco);
                                    const nroBancoNum = Number(banco.nroBanco);
                                    setPagoActual(prev => ({
                                        ...prev,
                                        banco: label,
                                        nroBanco: Number.isFinite(nroBancoNum) ? nroBancoNum : undefined,
                                    }));
                                    if (Number.isFinite(nroBancoNum) && nroBancoNum > 0) {
                                        setBancoCodigoInput(String(nroBancoNum));
                                    }
                                    setBancoNoEncontrado(false);
                                }
                                setOpenBancoDialog(false);
                            }}
                        >
                            Seleccionar
                        </Button>
                    </DialogActions>
                </Dialog>
            </form>

            {/* Modal de búsqueda de clientes */}
            <ClienteSearch
                open={openClienteSearch}
                onClose={() => setOpenClienteSearch(false)}
                onSelect={handleClienteSelect}
            />

            {aplicarNcComp && formulario.nroCliente > 0 && (
                <AplicarSaldoReciboDialog
                    open={aplicarNcDialogOpen}
                    onClose={handleCloseAplicarNcDialog}
                    reciboId={aplicarNcComp.nroFormulario!}
                    nroCliente={formulario.nroCliente}
                    nombreCliente={formulario.nombre || `Cliente ${formulario.nroCliente}`}
                    numeroComprobanteCredito={
                        formatNumeroComprobante(aplicarNcComp.nroComprobante, aplicarNcComp.tipoComprobante)
                    }
                    nroTipoFormularioCredito={aplicarNcComp.nroTipoFormulario ?? '3'}
                    importeTotalCredito={Math.abs(Number(aplicarNcComp.importe ?? aplicarNcComp.saldo ?? 0))}
                    onSuccess={() => {
                        handleCloseAplicarNcDialog();
                        setComprobantesSeleccionados(new Set());
                        setMontosAplicar(new Map());
                        setRefreshComprobantesKey((k) => k + 1);
                    }}
                />
            )}

                    {/* Botón Cancelar al final */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/tesoreria/recibos')}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default NuevoRecibo; 